import { NON_FUNGIBLE_FLOW_KINDS, TOKEN_FLOW_KINDS, NATIVE_COIN_FLOW_KINDS } from './chunk-JCX7FZKF.js';
import { unwrapData } from './chunk-HIWBYL4O.js';
import { protocolDisplayName, protocolFamilyName } from './chunk-RPCS62G5.js';
import { recordedConfidence } from './chunk-RMLZ5HCT.js';
import { REGION_ALIASES, resolveRegion, buildAutheHeaders, routingTokenForBearer, rewriteRegionApiUrl, rewriteUrl, unwrapEnvelope } from './chunk-BRBMP7SU.js';
import { delay, retryAsync } from './chunk-MZH5OJVQ.js';
import { AutheError, ErrorCode, errorCodeOf, getCodeSpec, toErrorResponse } from './chunk-HCZGW64N.js';
import { AMOUNT_BASIS } from './chunk-SRABNDFR.js';
import { isRecord, rowsFromWire } from './chunk-BFUJOKOR.js';
import { transactionFlag } from './chunk-EE4CQDCZ.js';
import { coerceRisk, riskLevelFromScore, RiskLevel, RISK_LEVEL_RANK } from './chunk-6B5KO6TH.js';
import { isRegionEnabled } from './chunk-3QN2VZ56.js';
import { decodeJwtPayload } from './chunk-LNDNXOJP.js';
import { singleflight } from './chunk-LVCDLPYA.js';
import { canAccessApplication } from './chunk-UEXNJL5R.js';
import { NO_CACHE, MemoryRequestCache, cacheKey, writeInvalidationFragment, asString, crossChainGroupingKey, formatTokenDisplay, tokenToUsd, formatUsd, shortenAddress, normalizeAddress, str, isValidAddress, formatUnits, shortenCompositeIdentifier, nativeTokenMeta, shortenHash } from './chunk-ZSUVGBJI.js';
import { SCENE_DETAIL_TIERS, SCENE_EMPTY_REASON_ATTR, parseGraphScene, mergedCount, mergedTxRefs, CONTESTED_ATTR, SceneSourceRefSchema } from './chunk-JWG7FVMX.js';
import { NATIVE_TOKEN, CDN_ASSET_ROOT, toCanonicalChainId, getChainGroupColors, brandGroupTint, getChainMetadata } from './chunk-LB3UVHT6.js';
import { createLogger } from './chunk-GPJ572YS.js';
import { brandClass, globalSingleton } from './chunk-N7SZ66QO.js';
import { z } from 'zod';

// src/storage/idb.ts
function expired(writtenAt, opts, now = Date.now()) {
  return opts?.maxAgeMs != null && now - writtenAt > opts.maxAgeMs;
}
function indexedDbAvailable() {
  try {
    return typeof indexedDB !== "undefined" && indexedDB !== null;
  } catch {
    return false;
  }
}
var DEFAULT_DB_NAME = "authe";
var DEFAULT_STORE_NAME = "kv";
function createMemoryKvStore() {
  const map = /* @__PURE__ */ new Map();
  return {
    async get(key, opts) {
      const entry = map.get(key);
      if (!entry) return void 0;
      if (expired(entry.t, opts)) {
        map.delete(key);
        return void 0;
      }
      return entry.v;
    },
    async set(key, value) {
      map.set(key, { v: value, t: Date.now() });
    },
    async delete(key) {
      map.delete(key);
    },
    async keys(prefix) {
      const all = [...map.keys()];
      return prefix ? all.filter((k) => k.startsWith(prefix)) : all;
    },
    async clearPrefix(prefix) {
      let removed = 0;
      for (const key of [...map.keys()]) {
        if (key.startsWith(prefix)) {
          map.delete(key);
          removed += 1;
        }
      }
      return removed;
    },
    async sweep(keep) {
      let removed = 0;
      for (const [key, entry] of [...map.entries()]) {
        if (!keep(key, entry.t)) {
          map.delete(key);
          removed += 1;
        }
      }
      return removed;
    }
  };
}
function createIdbKvStore(opts = {}) {
  const dbName = opts.dbName ?? DEFAULT_DB_NAME;
  const storeName = opts.storeName ?? DEFAULT_STORE_NAME;
  if (!indexedDbAvailable()) return createMemoryKvStore();
  let dbPromise = null;
  let fallback = null;
  function openDb() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve) => {
      try {
        const req = indexedDB.open(dbName, 1);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName);
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
        req.onblocked = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
    return dbPromise;
  }
  function memoryFallback() {
    if (!fallback) fallback = createMemoryKvStore();
    return fallback;
  }
  function tx(mode, run) {
    return openDb().then((db) => {
      if (!db) return void 0;
      return new Promise((resolve) => {
        try {
          const transaction = db.transaction(storeName, mode);
          const request = run(transaction.objectStore(storeName));
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => resolve(void 0);
          transaction.onerror = () => resolve(void 0);
          transaction.onabort = () => resolve(void 0);
        } catch {
          resolve(void 0);
        }
      });
    });
  }
  return {
    async get(key, opts2) {
      const db = await openDb();
      if (!db) return memoryFallback().get(key, opts2);
      const raw = await tx("readonly", (s2) => s2.get(key));
      if (!raw) return void 0;
      if (opts2?.maxAgeMs != null && (typeof raw.t !== "number" || expired(raw.t, opts2))) {
        await this.delete(key);
        return void 0;
      }
      return raw.v;
    },
    async set(key, value) {
      const db = await openDb();
      if (!db) return memoryFallback().set(key, value);
      const envelope = { v: value, t: Date.now() };
      await tx("readwrite", (s2) => s2.put(envelope, key));
    },
    async delete(key) {
      const db = await openDb();
      if (!db) return void memoryFallback().delete(key);
      await tx("readwrite", (s2) => s2.delete(key));
    },
    async keys(prefix) {
      const db = await openDb();
      if (!db) return memoryFallback().keys(prefix);
      const range = prefix != null ? IDBKeyRange.bound(prefix, `${prefix}\uFFFF`, false, false) : void 0;
      const result = await tx(
        "readonly",
        (s2) => s2.getAllKeys(range)
      );
      return (result ?? []).map(String);
    },
    async clearPrefix(prefix) {
      const db = await openDb();
      if (!db) return memoryFallback().clearPrefix(prefix);
      const matching = await this.keys(prefix);
      await Promise.all(matching.map((key) => this.delete(key)));
      return matching.length;
    },
    async sweep(keep) {
      const db = await openDb();
      if (!db) return memoryFallback().sweep(keep);
      return new Promise((resolve) => {
        let removed = 0;
        try {
          const transaction = db.transaction(storeName, "readwrite");
          const request = transaction.objectStore(storeName).openCursor();
          request.onsuccess = () => {
            const cursor = request.result;
            if (!cursor) return;
            const entry = cursor.value;
            const writtenAt = typeof entry?.t === "number" ? entry.t : 0;
            if (!keep(String(cursor.key), writtenAt)) {
              cursor.delete();
              removed += 1;
            }
            cursor.continue();
          };
          transaction.oncomplete = () => resolve(removed);
          transaction.onerror = () => resolve(removed);
          transaction.onabort = () => resolve(removed);
        } catch {
          resolve(removed);
        }
      });
    }
  };
}
var TX_BODY_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1e3;
var TX_STATIC_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1e3;
var ScopedTxCache = class _ScopedTxCache {
  constructor(store, identity, options = {}) {
    this.store = store;
    this.scope = new ScopedStorage(identity);
    this.bodyMaxAgeMs = options.bodyMaxAgeMs ?? TX_BODY_MAX_AGE_MS;
    this.staticMaxAgeMs = options.staticMaxAgeMs ?? TX_STATIC_MAX_AGE_MS;
  }
  store;
  static {
    brandClass(this, "storage/ScopedTxCache");
  }
  scope;
  bodyMaxAgeMs;
  staticMaxAgeMs;
  /** Cache key for one transaction body, content-addressed by its etag. */
  bodyKey(canonicalTxId, etag) {
    return this.scope.keyAt(
      ["body", canonicalTxId, etag ?? "noetag"].join(":"),
      this.scope.defaultLevel
    );
  }
  /** Cache key for a static/system blob, addressed by its deterministic S3 key. */
  staticKey(s3Key) {
    return this.scope.keyAt(["static", s3Key].join(":"), this.scope.defaultLevel);
  }
  /**
   * Cache key for one offloaded body slice, content-addressed by the SLICE's
   * own etag (each sibling S3 object has its own), so re-decoding one slice
   * never invalidates the cached core or a sibling slice.
   */
  sliceKey(canonicalTxId, slice, etag) {
    return this.scope.keyAt(
      ["slice", canonicalTxId, slice, etag ?? "noetag"].join(":"),
      this.scope.defaultLevel
    );
  }
  getBody(canonicalTxId, etag) {
    return this.store.get(this.bodyKey(canonicalTxId, etag), { maxAgeMs: this.bodyMaxAgeMs });
  }
  setBody(canonicalTxId, etag, value) {
    return this.store.set(this.bodyKey(canonicalTxId, etag), value);
  }
  getSlice(canonicalTxId, slice, etag) {
    return this.store.get(this.sliceKey(canonicalTxId, slice, etag), { maxAgeMs: this.bodyMaxAgeMs });
  }
  setSlice(canonicalTxId, slice, etag, value) {
    return this.store.set(this.sliceKey(canonicalTxId, slice, etag), value);
  }
  getStatic(s3Key) {
    return this.store.get(this.staticKey(s3Key), { maxAgeMs: this.staticMaxAgeMs });
  }
  setStatic(s3Key, value) {
    return this.store.set(this.staticKey(s3Key), value);
  }
  /**
   * Drop every cached entry at `level` and narrower for this identity — call on
   * logout (`"user"`) or when leaving a tenant (`"tenant"`), mirroring
   * `ScopedStorage.clear`.
   */
  clear(level) {
    const prefix = `${this.scope.keyAt("", level).replace(/:$/, "")}:`;
    return this.store.clearPrefix(prefix);
  }
  /**
   * Erase the whole cache — every identity, every tenant. What a sign-out
   * wants: {@link clear} only reaches the CURRENT tenant's keys, so a user who
   * visited three workspaces used to leave two of them on disk.
   */
  clearAll() {
    return this.store.sweep(() => false);
  }
  /**
   * Keep only what this identity may still read: drop every other identity's
   * entries and every expired one, in one pass over the store.
   *
   * Call once a session is established. Sign-out erases the departing user's
   * entries, but only when sign-out happens: a user who closes the browser,
   * or whose session simply lapses, leaves them on disk for whoever signs in
   * next. Scoped keys already stop that person READING them through the app;
   * this stops them sitting in the profile at all.
   *
   * With no signed-in user (`userId` absent) nothing is pruned by identity —
   * there is no "current" to keep, and wiping on an anonymous page would erase
   * a real user's cache on a login screen.
   */
  async prune(now = Date.now()) {
    if (!this.scope.identity.userId) return { removed: 0 };
    const own = `${this.scope.keyAt("", "user").replace(/:$/, "")}:`;
    const staticMarker = `${own}static:`;
    const removed = await this.store.sweep((key, writtenAt) => {
      if (!key.startsWith(own)) return false;
      const maxAge = key.startsWith(staticMarker) ? this.staticMaxAgeMs : this.bodyMaxAgeMs;
      return writtenAt > 0 && now - writtenAt <= maxAge;
    });
    return { removed };
  }
  /** Derive a cache bound to a different identity (e.g. after a tenant switch). */
  with(patch) {
    return new _ScopedTxCache(this.store, { ...this.scope.identity, ...patch }, {
      bodyMaxAgeMs: this.bodyMaxAgeMs,
      staticMaxAgeMs: this.staticMaxAgeMs
    });
  }
};
function createScopedTxCache(identity, store, options) {
  return new ScopedTxCache(store ?? sharedIdbKvStore(), identity, options);
}
var sharedStore = globalSingleton("storage/idb", () => ({ store: null }));
function sharedIdbKvStore() {
  if (!sharedStore.store) sharedStore.store = createIdbKvStore();
  return sharedStore.store;
}

// src/storage/cdn-cookies.ts
var cdn = globalSingleton("storage/cdn-session", () => ({
  config: {},
  session: null,
  inflight: null,
  /** Epoch seconds until which a cached NEGATIVE decision is reused (see `negativeTtlSec`). */
  negativeUntil: 0
}));
var DEFAULT_NEGATIVE_TTL_SEC = 30;
function configureCdnDelivery(next) {
  cdn.config = { ...cdn.config, ...next };
  cdn.session = null;
  cdn.inflight = null;
  cdn.negativeUntil = 0;
}
function clearCdnSession() {
  cdn.session = null;
  cdn.inflight = null;
  cdn.negativeUntil = 0;
}
function invalidateCdnSession() {
  cdn.session = null;
  cdn.inflight = null;
  cdn.negativeUntil = 0;
  cdn.config.persist?.clear();
}
function getCdnSession() {
  return cdn.session;
}
function cdnBaseUrl() {
  return cdn.config.cdnBaseUrl ?? cdn.session?.cdnBaseUrl;
}
function nowSec() {
  return Math.floor(Date.now() / 1e3);
}
function valid(s2, marginSec) {
  if (!s2 || !s2.signedCookieDelivery) return false;
  if (!s2.expiresAt) return true;
  return s2.expiresAt - marginSec > nowSec();
}
async function ensureCdnSession() {
  const margin = cdn.config.refreshMarginSec ?? 60;
  if (valid(cdn.session, margin)) return cdn.session;
  if (cdn.session && !cdn.session.signedCookieDelivery && nowSec() < cdn.negativeUntil) return cdn.session;
  if (!cdn.session) {
    const persisted = cdn.config.persist?.load() ?? null;
    if (valid(persisted, margin)) {
      cdn.session = { ...persisted, cdnBaseUrl: persisted.cdnBaseUrl ?? cdn.config.cdnBaseUrl };
      return cdn.session;
    }
  }
  if (!cdn.config.mint) {
    cdn.session = { signedCookieDelivery: false, reason: "no_mint_configured", cdnBaseUrl: cdn.config.cdnBaseUrl };
    markNegative();
    return cdn.session;
  }
  if (!cdn.inflight) {
    const mint = cdn.config.mint;
    cdn.inflight = (async () => {
      try {
        const r = await mint();
        cdn.session = { ...r, cdnBaseUrl: r.cdnBaseUrl ?? cdn.config.cdnBaseUrl };
        if (cdn.session.signedCookieDelivery) cdn.config.persist?.save(cdn.session);
        else markNegative();
      } catch (err) {
        cdn.session = {
          signedCookieDelivery: false,
          reason: err instanceof Error ? err.message : "mint_failed",
          cdnBaseUrl: cdn.config.cdnBaseUrl
        };
        markNegative();
      } finally {
        cdn.inflight = null;
      }
      return cdn.session;
    })();
  }
  return cdn.inflight;
}
function markNegative() {
  const ttl = cdn.config.negativeTtlSec ?? DEFAULT_NEGATIVE_TTL_SEC;
  cdn.negativeUntil = ttl > 0 ? nowSec() + ttl : 0;
}
function signedCdnUrl(key, base) {
  return `${base.replace(/\/+$/, "")}/${String(key).replace(/^\//, "")}`;
}
var PUBLIC_KEY_SEGMENT = /(^|\/)public\//;
function isPublicCdnKey(key) {
  return PUBLIC_KEY_SEGMENT.test(String(key).replace(/^\/+/, ""));
}
var CdnAccessDeniedError = class extends Error {
  static {
    brandClass(this, "storage/CdnAccessDeniedError");
  }
  /** The offending CDN object key. */
  key;
  /** Stable code for UI branching / telemetry. */
  code = "cdn_cross_scope_denied";
  constructor(key, message) {
    super(message ?? `CDN object is outside your access scope: ${key}`);
    this.name = "CdnAccessDeniedError";
    this.key = key;
  }
};
function normalizeCdnKey(key) {
  return String(key).replace(/^\/+/, "");
}
function prefixCovers(key, prefix) {
  const p = normalizeCdnKey(prefix).replace(/\/+$/, "");
  if (!p) return false;
  return key === p || key.startsWith(`${p}/`);
}
var PRIVATE_BODY_ROOT = "pipeline/bodies/private/";
var PRIVATE_ASSET_ROOT = "assets/private/";
function isPrivateCdnKey(key) {
  return !isPublicCdnKey(key);
}
function isPrivateAssetCdnKey(key) {
  return normalizeCdnKey(key).startsWith(PRIVATE_ASSET_ROOT);
}
function cdnKeyDenialReason(key, session) {
  if (isPublicCdnKey(key)) return null;
  const k = normalizeCdnKey(key);
  if (k.startsWith(PRIVATE_ASSET_ROOT)) {
    const readable2 = session?.privateAssetPrefixes ?? [];
    if (readable2.length === 0) return null;
    return readable2.some((p) => prefixCovers(k, p)) ? null : "cross_scope_denied";
  }
  if (!k.startsWith(PRIVATE_BODY_ROOT)) return null;
  const readable = [...session?.privatePrefixes ?? [], ...session?.sharedPrefixes ?? []];
  if (readable.length === 0) return null;
  if (readable.some((p) => prefixCovers(k, p))) return null;
  return "cross_scope_denied";
}
function isCdnKeyReadable(key, session) {
  return cdnKeyDenialReason(key, session) === null;
}
async function fetchSignedCdnJson(key, init) {
  const isPublic = isPublicCdnKey(key);
  let base;
  if (isPublic) {
    base = cdn.config.cdnBaseUrl ?? getCdnSession()?.cdnBaseUrl;
  } else {
    const s2 = await ensureCdnSession();
    base = s2.cdnBaseUrl ?? cdn.config.cdnBaseUrl;
    if (!s2.signedCookieDelivery || !base) {
      throw new Error(`signed CDN delivery unavailable${s2.reason ? ` (${s2.reason})` : ""}`);
    }
    if (cdnKeyDenialReason(key, s2)) throw new CdnAccessDeniedError(key);
  }
  if (!base) throw new Error("CDN delivery unavailable (no_cdn_base_url)");
  const res = await fetch(signedCdnUrl(key, base), {
    ...init,
    // Public: send NO cookies (wildcard ACAO forbids credentials).
    // Private: send the CloudFront signed cookies.
    credentials: isPublic ? "omit" : "include",
    headers: { Accept: "application/json", ...init?.headers ?? {} }
  });
  if (!res.ok) {
    if (!isPublic && (res.status === 401 || res.status === 403)) invalidateCdnSession();
    throw new Error(`${isPublic ? "public" : "signed"} CDN fetch failed: ${res.status} ${res.statusText}`);
  }
  return await res.json();
}
async function prepareSignedCdnImage(key) {
  if (isPublicCdnKey(key)) {
    const base2 = cdn.config.cdnBaseUrl ?? getCdnSession()?.cdnBaseUrl;
    if (!base2) return { denied: false, reason: "no_cdn_base_url" };
    return { src: signedCdnUrl(key, base2), crossOrigin: "anonymous", denied: false };
  }
  const s2 = await ensureCdnSession();
  const base = s2.cdnBaseUrl ?? cdn.config.cdnBaseUrl;
  if (!s2.signedCookieDelivery || !base) {
    return { denied: false, reason: `signed_delivery_unavailable${s2.reason ? `:${s2.reason}` : ""}` };
  }
  if (cdnKeyDenialReason(key, s2)) return { denied: true, reason: "cross_scope_denied" };
  return { src: signedCdnUrl(key, base), crossOrigin: "use-credentials", denied: false };
}

// src/storage/loader.ts
async function defaultFetchJson(url, init) {
  const res = await fetch(url, init);
  if (!res.ok) {
    throw new Error(`fetch ${url} failed: ${res.status} ${res.statusText}`);
  }
  return res.json();
}
function versionedKey(key, etag) {
  return etag ? `${key}?v=${encodeURIComponent(etag)}` : key;
}
function createEnvelopeBodyLoader(config) {
  const fetchJson = config.fetchJson ?? defaultFetchJson;
  function resolveBodyUrl(s3Key, canonicalTxId, slice) {
    if (config.resolveBodyUrl) return config.resolveBodyUrl(s3Key, canonicalTxId, slice);
    if (config.cdn) return cdnUrlForKey(s3Key, config.cdn);
    throw new Error(
      "createEnvelopeBodyLoader: no resolveBodyUrl and no cdn config \u2014 cannot resolve an S3 key to a URL."
    );
  }
  async function fetchOne(key, canonicalTxId, slice) {
    if (config.fetchKey) return config.fetchKey(key);
    return fetchJson(await resolveBodyUrl(key, canonicalTxId, slice));
  }
  async function fetchFirstReadable(keys, opts = {}) {
    for (const key of keys) {
      try {
        const payload = await fetchOne(
          versionedKey(key, opts.etag),
          opts.canonicalTxId,
          opts.slice
        );
        return { key, payload };
      } catch (err) {
        if (err instanceof CdnAccessDeniedError) continue;
        throw err;
      }
    }
    return null;
  }
  function loadBody(header, inlineBody) {
    const { canonical_tx_id, s3_etag } = header;
    const fetchBody = async (s3Key) => {
      const cached = await config.cache.getBody(canonical_tx_id, s3_etag);
      if (cached) return cached;
      const url = await resolveBodyUrl(s3Key, canonical_tx_id);
      const body = await fetchJson(url);
      await config.cache.setBody(canonical_tx_id, s3_etag, body);
      return body;
    };
    if (inlineBody) {
      void config.cache.setBody(canonical_tx_id, s3_etag, inlineBody);
      return loadEnvelope(header, fetchBody, inlineBody).body;
    }
    return loadEnvelope(header, fetchBody).body;
  }
  async function loadSlice(header, slice) {
    const ref = header.body_slices?.[slice];
    if (!ref || !header.s3_key) return void 0;
    const key = sliceKey(header.s3_key, slice);
    if (!key) return void 0;
    const sliceEtag = ref.etag ?? header.s3_etag ?? null;
    const cached = await config.cache.getSlice(header.canonical_tx_id, slice, sliceEtag);
    if (cached !== void 0) return cached;
    const url = await resolveBodyUrl(key, header.canonical_tx_id, slice);
    const payload = await fetchJson(url);
    await config.cache.setSlice(header.canonical_tx_id, slice, sliceEtag, payload);
    return payload;
  }
  async function loadFullBody(header, opts = {}) {
    const core = await loadBody(header, opts.inlineBody).get();
    const manifest = header.body_slices ?? {};
    const wanted = (opts.include ?? Object.keys(manifest)).filter(
      (slice) => slice !== SLICE_CORE && manifest[slice]
    );
    if (wanted.length === 0) return core;
    const merged = cloneBody(core);
    await Promise.all(
      wanted.map(async (slice) => {
        const payload = await loadSlice(header, slice);
        if (payload !== void 0) {
          mergeSlice(merged, slice, payload);
        }
      })
    );
    return merged;
  }
  async function loadCore(ref) {
    const { canonicalTxId, keys, etag } = ref;
    if (canonicalTxId) {
      const cached = await config.cache.getBody(canonicalTxId, etag);
      if (cached !== void 0) return { key: keys[0] ?? "", body: cached };
    }
    const hit = await fetchFirstReadable(keys, { canonicalTxId, etag });
    if (!hit) return null;
    if (canonicalTxId) await config.cache.setBody(canonicalTxId, etag, hit.payload);
    return { key: hit.key, body: hit.payload };
  }
  async function loadStatic(staticS3Key) {
    const cached = await config.cache.getStatic(staticS3Key);
    if (cached !== void 0) return cached;
    const url = config.cdn ? cdnUrlForKey(staticS3Key, config.cdn) : await resolveBodyUrl(staticS3Key);
    const data = await fetchJson(url);
    await config.cache.setStatic(staticS3Key, data);
    return data;
  }
  return { loadBody, loadCore, loadSlice, loadFullBody, loadStatic, fetchFirstReadable };
}
function cloneBody(body) {
  const sc = globalThis.structuredClone;
  if (typeof sc === "function") return sc(body);
  return JSON.parse(JSON.stringify(body));
}

// src/storage/bulk.ts
var DEFAULTS = { concurrency: 4, maxAttempts: 2, flushMs: 120 };
function defaultScheduler(run, ms) {
  const id = setTimeout(run, ms);
  return () => clearTimeout(id);
}
var BulkLoader = class {
  static {
    brandClass(this, "storage/BulkLoader");
  }
  results = /* @__PURE__ */ new Map();
  claimed = /* @__PURE__ */ new Set();
  attempts = /* @__PURE__ */ new Map();
  listeners = /* @__PURE__ */ new Set();
  queue = [];
  cancelFlush = null;
  running = 0;
  stopped = false;
  concurrency;
  maxAttempts;
  flushMs;
  schedule;
  constructor(options = {}) {
    this.concurrency = Math.max(1, options.concurrency ?? DEFAULTS.concurrency);
    this.maxAttempts = Math.max(1, options.maxAttempts ?? DEFAULTS.maxAttempts);
    this.flushMs = options.flushMs ?? DEFAULTS.flushMs;
    this.schedule = options.scheduleFlush ?? defaultScheduler;
  }
  /** Notified once per batch of resolutions, not once per resolution. */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => void this.listeners.delete(listener);
  }
  /** A settled result, or `undefined` when the key hasn't been asked yet. */
  get(key) {
    return this.results.get(key);
  }
  /** Snapshot of everything settled so far. */
  snapshot() {
    return new Map(this.results);
  }
  /** True while at least one load is in flight. */
  get isLoading() {
    return this.running > 0;
  }
  /**
   * Replace the queue and start working it.
   *
   * Replacing rather than appending because the caller's list is the truth: rows
   * scroll out of the feed, and work queued for a row nobody can see any more is
   * work taken from one they can. Already-settled and in-flight keys are left
   * alone, so a list that grew by one row does not restart the other hundred.
   */
  enqueue(tasks) {
    if (this.stopped) return;
    this.queue = tasks.filter((task) => !this.results.has(task.key));
    this.pump();
  }
  /**
   * Stop taking new work.
   *
   * In-flight loads are deliberately NOT aborted: the list changing (a new
   * transaction, a finality tick) shouldn't throw away a body already on the
   * wire, and it will be wanted by whatever replaces this loader.
   */
  stop() {
    this.stopped = true;
    this.cancelFlush?.();
    this.cancelFlush = null;
    this.listeners.clear();
  }
  take() {
    for (const task of this.queue) {
      if (this.results.has(task.key) || this.claimed.has(task.key)) continue;
      if ((this.attempts.get(task.key) ?? 0) >= this.maxAttempts) {
        this.results.set(task.key, null);
        this.notify();
        continue;
      }
      this.claimed.add(task.key);
      return task;
    }
    return null;
  }
  pump() {
    while (this.running < this.concurrency) {
      const task = this.take();
      if (!task) return;
      void this.run(task);
    }
  }
  async run(task) {
    this.attempts.set(task.key, (this.attempts.get(task.key) ?? 0) + 1);
    this.running += 1;
    if (this.running === 1) this.notify();
    try {
      this.results.set(task.key, await task.load());
      this.notify();
    } catch {
    } finally {
      this.claimed.delete(task.key);
      this.running -= 1;
      if (this.running === 0) this.notify();
      if (!this.stopped) this.pump();
    }
  }
  notify() {
    if (this.cancelFlush || this.stopped) return;
    this.cancelFlush = this.schedule(() => {
      this.cancelFlush = null;
      for (const listener of this.listeners) listener();
    }, this.flushMs);
  }
};
function capDistinct(items, keyOf, max) {
  const taken = [];
  const keys = [];
  const seen = /* @__PURE__ */ new Set();
  let eligible = 0;
  for (const item of items) {
    const key = keyOf(item);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    eligible += 1;
    if (taken.length >= max) continue;
    taken.push(item);
    keys.push(key);
  }
  return { taken, keys, dropped: eligible - taken.length };
}

// src/config/endpoints.ts
var ApiEndpointMode = {
  CLOUDFRONT: "cloudfront",
  GATEWAY: "gateway"
};
function stripTrailingSlash(url) {
  return url.replace(/\/+$/, "");
}
function resolveApiBaseUrl(config, mode) {
  const resolved = mode ?? config.defaultMode ?? ApiEndpointMode.CLOUDFRONT;
  if (resolved === ApiEndpointMode.GATEWAY && config.apiGatewayUrl) {
    return stripTrailingSlash(config.apiGatewayUrl);
  }
  return stripTrailingSlash(config.cloudFrontUrl);
}

// src/config/debug.ts
var DEBUG_QUERY_KEYS = {
  endpoint: "api",
  region: "region",
  debug: "debug"
};
function debugStickyKey() {
  return storageKey("config", "debug");
}
function coerceMode(value) {
  if (value === ApiEndpointMode.GATEWAY || value === "gw" || value === "apigw") return ApiEndpointMode.GATEWAY;
  if (value === ApiEndpointMode.CLOUDFRONT || value === "cf" || value === "cdn") return ApiEndpointMode.CLOUDFRONT;
  return void 0;
}
function parseDebugParams(search) {
  const qs = search ?? (typeof window !== "undefined" ? window.location.search : "");
  const params2 = new URLSearchParams(qs);
  const raw = {};
  for (const [k, v] of params2.entries()) raw[k] = v;
  const out = { raw };
  const mode = coerceMode(params2.get(DEBUG_QUERY_KEYS.endpoint));
  if (mode) out.endpointMode = mode;
  const region = params2.get(DEBUG_QUERY_KEYS.region);
  if (region) out.region = region;
  const debug = params2.get(DEBUG_QUERY_KEYS.debug);
  if (debug != null) out.verbose = debug !== "0" && debug !== "false";
  return out;
}
function getDebugParams(search) {
  const fromUrl = parseDebugParams(search);
  const sticky = readLocalJson(debugStickyKey(), null);
  if (fromUrl.verbose) {
    writeLocalJson(debugStickyKey(), fromUrl);
    return fromUrl;
  }
  if (fromUrl.verbose === false) {
    removeLocalKey(debugStickyKey());
    return fromUrl;
  }
  if (!sticky) return fromUrl;
  return { ...sticky, ...fromUrl, raw: { ...sticky.raw, ...fromUrl.raw } };
}
function isDebugEnabled(search) {
  return getDebugParams(search).verbose === true;
}

// src/api/debug.ts
var FIELD_KINDS = /* @__PURE__ */ new Set([
  "text",
  "id",
  "timestamp",
  "duration_ms",
  "bytes",
  "number",
  "ratio",
  "json"
]);
function rec(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : void 0;
}
function coerceField(raw) {
  const o = rec(raw);
  const label = asString(o?.label);
  if (!o || !label) return null;
  const kind = asString(o.kind);
  return {
    key: asString(o.key) ?? label,
    label,
    // `value` is read with `in` rather than a truthiness test: `0`, `false` and
    // `null` are all legitimate diagnostics.
    value: "value" in o ? o.value : null,
    kind: kind && FIELD_KINDS.has(kind) ? kind : "text",
    ...asString(o.hint) ? { hint: asString(o.hint) } : {}
  };
}
function coerceGroup(raw) {
  const o = rec(raw);
  const label = asString(o?.label);
  if (!o || !label) return null;
  const fields = (Array.isArray(o.fields) ? o.fields : []).map(coerceField).filter((f) => f !== null);
  if (fields.length === 0) return null;
  return { key: asString(o.key) ?? label, label, fields };
}
function coerceResource(raw) {
  const o = rec(raw);
  const resource = asString(o?.resource);
  if (!o || !resource) return null;
  const groups = (Array.isArray(o.groups) ? o.groups : []).map(coerceGroup).filter((g) => g !== null);
  if (groups.length === 0) return null;
  return {
    resource,
    label: asString(o.label) ?? resource,
    id: asString(o.id) ?? null,
    groups
  };
}
function readResponseDebug(envelope) {
  const block = rec(rec(envelope)?.debug);
  if (!block) return void 0;
  const resources = (Array.isArray(block.resources) ? block.resources : []).map(coerceResource).filter((r) => r !== null);
  return { ...block, ...resources.length ? { resources } : {} };
}
function readDebugResources(envelope) {
  return readResponseDebug(envelope)?.resources ?? [];
}
var DebugResourceCaptureSink = class {
  byKind = /* @__PURE__ */ new Map();
  listeners = /* @__PURE__ */ new Set();
  snapshot = [];
  /**
   * Read any resources off `envelope` and record them against `path`.
   *
   * Cheap and total: a response with no debug block (the normal case, and every
   * response when debug is off) costs one property read and notifies nobody.
   */
  capture(path, envelope) {
    const resources = readDebugResources(envelope);
    if (resources.length === 0) return;
    const at = Date.now();
    for (const resource of resources) {
      this.byKind.set(resource.resource, { ...resource, path, at });
    }
    this.publish();
  }
  /** Drop everything (navigation: the previous page's record is off screen). */
  clear() {
    if (this.byKind.size === 0) return;
    this.byKind.clear();
    this.publish();
  }
  /** Everything captured, newest-captured last. Stable between changes. */
  list = () => this.snapshot;
  subscribe = (listener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  publish() {
    this.snapshot = [...this.byKind.values()];
    for (const listener of this.listeners) listener();
  }
};
var debugResourceSink = globalSingleton(
  "api/debug-resource-sink",
  () => new DebugResourceCaptureSink()
);

// src/api/client.ts
var log = createLogger("fe-commons:api");
var ALIAS_TO_REGION = {
  pdx: "us-west-2",
  iad: "us-east-1",
  fra: "eu-central-1",
  dub: "eu-west-1"
};
function pickActiveRegionUrl(primary, backup, now = /* @__PURE__ */ new Date()) {
  if (!backup) return primary;
  return now.getUTCMinutes() % 5 > 2 ? backup : primary;
}
function buildQueryString(query) {
  if (!query) return "";
  const params2 = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value == null || value === "") continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item == null || item === "") continue;
        params2.append(key, String(item));
      }
    } else {
      params2.set(key, String(value));
    }
  }
  const s2 = params2.toString();
  return s2 ? `?${s2}` : "";
}
var RetryableHttpError = class extends Error {
  constructor(envelope, retryAfterMs) {
    super(envelope.error.message);
    this.envelope = envelope;
    this.retryAfterMs = retryAfterMs;
    this.name = "RetryableHttpError";
  }
  envelope;
  retryAfterMs;
};
function isRetryableResponse(status, error) {
  const code = errorCodeOf(error);
  if (code) {
    const spec = getCodeSpec(code);
    if (spec) return spec.retryable;
  }
  const said = error.retryable;
  if (typeof said === "boolean") return said;
  return status >= 500 || status === 429;
}
var EXPIRY_SKEW_MS = 3e4;
function sentBearer(headers) {
  const value = headers.Authorization ?? headers.authorization;
  if (!value) return null;
  const match = /^Bearer\s+(.+)$/i.exec(value);
  return match?.[1] ?? null;
}
function bearerExpired(bearer) {
  if (!bearer) return true;
  const exp = decodeJwtPayload(bearer)?.exp;
  if (typeof exp !== "number") return false;
  return exp * 1e3 <= Date.now() + EXPIRY_SKEW_MS;
}
function isAuthTokenExpired(env, bearer) {
  if (env.statusCode !== 401 && env.statusCode !== 403) return false;
  if (env.statusCode === 401) return true;
  if (bearerExpired(bearer)) return true;
  const msg = (env.error?.message ?? "").toLowerCase();
  return msg.includes("expired") || msg.includes("signature");
}
function parseRetryAfterMs(response) {
  const ms = response.headers.get("Retry-After-Ms");
  if (ms) {
    const n = Number(ms);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  const ra = response.headers.get("Retry-After");
  if (ra) {
    const secs = Number(ra);
    if (Number.isFinite(secs)) return Math.max(0, secs * 1e3);
    const at = Date.parse(ra);
    if (!Number.isNaN(at)) return Math.max(0, at - Date.now());
  }
  return void 0;
}
var ApiClient = class {
  static {
    brandClass(this, "api/ApiClient");
  }
  config;
  cache;
  cacheTtlMs;
  /** Coalesces concurrent identical cacheable GETs into a single network call. */
  inflight = singleflight();
  /** Collapses concurrent token-expiry recoveries into a single refresh/re-login. */
  authRecovery = singleflight();
  /**
   * The CANONICAL path (+ query) each cached GET was requested with, by cache
   * key. See {@link invalidateMatching}: callers name reads by the path they
   * requested, while the key is the resolved URL.
   */
  canonicalByKey = /* @__PURE__ */ new Map();
  /** Invalidation also fences in-flight reads; older executions cannot refill the cache. */
  cacheGeneration = 0;
  cacheInvalidations = [];
  cacheHistoryFloor = 0;
  /** The bearer the last request was built with (routes blue/green, see resolveUrl). */
  lastBearer = null;
  /** See {@link onErrorEnvelope}. */
  errorEnvelopeListeners = /* @__PURE__ */ new Set();
  constructor(config) {
    this.config = config;
    this.cache = config.cache ?? (config.cacheTtlMs === 0 ? NO_CACHE : new MemoryRequestCache());
    this.cacheTtlMs = config.cacheTtlMs ?? 3e4;
    if (!isRegionEnabled(this.regionToFullName(config.region))) {
      log.warn(
        `region ${config.region} is not marked enabled in commons/utils/aws_region_helpers; requests will still attempt.`
      );
    }
  }
  regionToFullName(regionOrAlias) {
    if (regionOrAlias.includes("-")) return regionOrAlias;
    return ALIAS_TO_REGION[regionOrAlias] ?? regionOrAlias;
  }
  resolveBaseUrl() {
    return pickActiveRegionUrl(this.config.baseUrl, this.config.backupBaseUrl);
  }
  /** Verbose-debug flag: explicit config wins, else fe-commons URL/sticky flag. */
  debugOn() {
    return this.config.debug ?? isDebugEnabled();
  }
  /**
   * Build a {@link ResolvedRegion} pinning a single region (alias or full AWS
   * region), bypassing the token-driven resolution. Used by the per-request
   * `region` override so a write can be fanned out to a specific region's host.
   */
  overrideResolvedRegion(override) {
    if (!override) return void 0;
    const entry = REGION_ALIASES.find((r) => r.alias === override || r.region === override);
    const alias = entry?.alias ?? override;
    const region = entry?.region ?? override;
    return {
      useRegion: region,
      useRegionAlias: alias,
      useBackupRegion: region,
      useBackupRegionAlias: alias
    };
  }
  async buildHeaders(extra, regionOverride) {
    const override = this.overrideResolvedRegion(regionOverride);
    if (this.config.routing) {
      const token2 = this.config.routing.getToken();
      if (token2) {
        const resolved = override ?? resolveRegion(token2, this.config.routing);
        const headers2 = buildAutheHeaders(token2, this.config.routing, resolved, {
          "Content-Type": "application/json",
          ...extra ?? {}
        });
        const live = await Promise.resolve(this.config.getAccessToken()).catch(() => null);
        if (live) {
          headers2.Authorization = `Bearer ${live}`;
          this.lastBearer = live;
        }
        if (this.debugOn()) headers2["X-Debug"] = "1";
        return headers2;
      }
    }
    const token = await Promise.resolve(this.config.getAccessToken());
    const region = override?.useRegion ?? this.config.region;
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Source-Platform": this.config.application,
      "Source-Region": region,
      "Destination-Region": region,
      ...extra ?? {}
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (this.debugOn()) headers["X-Debug"] = "1";
    return headers;
  }
  /** Resolve the full request URL — routing mode applies the verbatim rewrite. */
  resolveUrl(rawPath, query, method = "GET", regionOverride) {
    const path = this.config.rewritePath ? this.config.rewritePath(rawPath, method) : rawPath;
    const qs = buildQueryString(query);
    if (this.config.routing) {
      const snapshot = this.config.routing.getToken();
      const token = snapshot && this.lastBearer ? routingTokenForBearer(snapshot, this.lastBearer, this.config.routing.claimsNamespace) : snapshot;
      if (token) {
        const resolved = this.overrideResolvedRegion(regionOverride) ?? resolveRegion(token, this.config.routing);
        if (this.config.regionApiRouting) {
          const regional = rewriteRegionApiUrl(`${path}${qs}`, token, this.config.routing, resolved, method);
          if (regional) return regional;
        } else {
          const absolute = /^https?:\/\//.test(path) ? path : `${this.resolveBaseUrl().replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
          return rewriteUrl(`${absolute}${qs}`, token, this.config.routing, resolved);
        }
      }
    }
    return `${this.resolveBaseUrl()}${path}${qs}`;
  }
  /**
   * Run the {@link ApiClientConfig.onAuthError} hook (de-duped across concurrent
   * expiries) and report whether the caller should retry. A throwing hook is
   * treated as "don't retry" so a recovery failure never masks the original
   * auth error.
   */
  async recoverFromAuthError() {
    const hook = this.config.onAuthError;
    if (!hook) return false;
    return this.authRecovery.run("auth", async () => {
      try {
        return await Promise.resolve(hook()) === true;
      } catch (error) {
        log.warn("auth recovery hook threw", {
          error: error instanceof Error ? error.message : String(error)
        });
        return false;
      }
    });
  }
  /**
   * Refuse, before any request, a caller whose auth type may not use this app.
   *
   * Throws a PERMISSION error (`PERM001`), not a bare `Error`. A bare `Error`
   * carries no code or status, so `toErrorResponse` could only classify it as
   * `INT001` — and the private share viewer, whose `application` was once
   * missing from the gateway-auth table so this fired for every user, told each
   * of them "Internal server error / contact support if persistent". It was
   * neither internal nor on a server: it never left the browser, retrying could
   * not help, and support was the wrong person to ask. A refusal is a refusal.
   */
  assertCanAccess() {
    if (this.config.authType === void 0 || this.config.authType === null) return;
    if (!canAccessApplication(this.config.authType, this.config.application)) {
      throw new AutheError(ErrorCode.PERM001, {
        message: `Auth type "${this.config.authType}" cannot access application "${this.config.application}"`,
        details: { authType: this.config.authType, application: this.config.application }
      });
    }
  }
  /**
   * Be told about every `ok:false` envelope this client resolves, from any
   * caller. Returns the unsubscribe.
   *
   * For a host that must react to a KIND of refusal wherever it happens —
   * a plan refusal (`PERM003`) means the host's copy of the plan is stale, and
   * the write that met it may be in any package, behind any adapter, with or
   * without a caller that inspects the error. Every one of those writes rides
   * this client, so this is the one place that sees them all.
   *
   * Fired once per network execution (coalesced callers share one), after
   * retries and auth recovery have settled, and never for a cache hit (only
   * successes are cached). A listener that throws is logged and skipped: an
   * observer must not turn into the request's failure.
   */
  onErrorEnvelope(listener) {
    this.errorEnvelopeListeners.add(listener);
    return () => {
      this.errorEnvelopeListeners.delete(listener);
    };
  }
  notifyErrorEnvelope(response, method, path) {
    if (response.ok !== false || this.errorEnvelopeListeners.size === 0) return response;
    for (const listener of [...this.errorEnvelopeListeners]) {
      try {
        listener(response, { method, path });
      } catch (error) {
        log.warn("error-envelope listener threw", { path, method, error: String(error) });
      }
    }
    return response;
  }
  async request(path, opts = {}) {
    this.assertCanAccess();
    const method = opts.method ?? "GET";
    const url = this.resolveUrl(path, opts.query, method);
    const body = opts.rawBody != null ? opts.rawBody : opts.body == null ? void 0 : JSON.stringify(opts.body);
    const ttl = opts.cacheTtlMs ?? (method === "GET" ? this.cacheTtlMs : 0);
    const key = cacheKey(method, url, body);
    const cacheable = method === "GET" && ttl > 0;
    const coalescable = method === "GET";
    const cacheGeneration = this.cacheGeneration;
    if (cacheable) {
      const hit = this.cache.get(key);
      if (hit) return hit;
    }
    if (coalescable) {
      const sharedOpts = { ...opts, signal: void 0 };
      const run = () => this.execute({ path, query: opts.query, method, body, ttl, key, opts: sharedOpts, cacheGeneration });
      return this.inflight.run(`${cacheGeneration}:${key}`, run);
    }
    return this.execute({ path, query: opts.query, method, body, ttl, key, opts, cacheGeneration });
  }
  /**
   * Resolve the request URL + headers together, waiting for the auth token to be
   * ready. Two cold-load hazards this addresses:
   *  - **routing mode** resolves the URL from the JWT (region/continent host
   *    swap). If the token hasn't been pushed to the router yet, the URL froze to
   *    the fallback baseUrl (the `api.` edge — no localhost CORS → status 0). We
   *    wait (bounded) for `routing.getToken()` before resolving, then build the
   *    correct regional URL.
   *  - **non-routing mode** reads the access token async; `buildHeaders` is
   *    retried so a transient `getAccessToken()` rejection doesn't lose the
   *    request.
   * Both degrade gracefully (proceed with the fallback) once the budget is spent,
   * and honour abort. Called per network attempt so a token landing mid-flight is
   * picked up on the next try.
   */
  async resolveRequest(path, query, method, extra, signal, regionOverride) {
    const budget = this.config.authRetries ?? 5;
    if (this.config.routing) {
      for (let attempt = 0; attempt < budget && !this.config.routing.getToken(); attempt++) {
        if (signal?.aborted) break;
        log.warn("waiting for routing token before resolving URL", { path, method, attempt });
        await delay(Math.min(150 * 2 ** attempt, 2e3), signal);
      }
    }
    const headers = await retryAsync(() => this.buildHeaders(extra, regionOverride), {
      retries: budget,
      baseMs: 150,
      maxMs: 2e3,
      signal,
      shouldRetry: (_error, attempt) => {
        if (signal?.aborted) return false;
        log.warn("retrying access-token acquisition", { path, method, attempt });
        return true;
      }
    });
    const url = this.resolveUrl(path, query, method, regionOverride);
    if (this.config.requestSigner) {
      Object.assign(headers, await this.config.requestSigner({ url, method }));
    }
    return { url, headers };
  }
  /** One network execution (shared by coalesced callers), with backoff retries. */
  async execute(ctx) {
    const { path, query, method, body, ttl, key, opts } = ctx;
    const retries = opts.retries ?? this.config.retries ?? 1;
    const timeoutMs = opts.timeoutMs ?? this.config.timeoutMs ?? 3e4;
    const backoff = { baseMs: 250, maxMs: 5e3, ...this.config.retryBackoff, ...opts.retryBackoff };
    let retryAfterMs = 0;
    let bearer = null;
    const runAttempts = () => retryAsync(
      async () => {
        const { url, headers } = await this.resolveRequest(path, query, method, opts.headers, opts.signal, opts.region);
        bearer = sentBearer(headers);
        return this.attempt(url, method, headers, body, opts.signal, timeoutMs, opts.exposeHeaders, opts.credentials, opts.responseType);
      },
      {
        retries,
        ...backoff,
        signal: opts.signal,
        // `attempt` returns terminal envelopes (2xx + non-retryable 4xx/5xx)
        // and throws on retryable failures; stop only when the caller aborts.
        shouldRetry: () => !opts.signal?.aborted,
        onRetry: (error, attempt, delayMs) => {
          retryAfterMs = error instanceof RetryableHttpError ? error.retryAfterMs ?? 0 : 0;
          log.warn("retrying request", { path, method, attempt, delayMs: Math.max(delayMs, retryAfterMs) });
        },
        sleep: (ms, signal) => delay(Math.max(ms, retryAfterMs), signal)
      }
    );
    try {
      let envelope = await runAttempts();
      if (!envelope.ok && !opts.signal?.aborted && this.config.onAuthError && isAuthTokenExpired(envelope, bearer)) {
        const retry = await this.recoverFromAuthError();
        if (retry && !opts.signal?.aborted) {
          log.warn("retrying request after auth-token refresh", { path, method });
          envelope = await runAttempts();
        }
      }
      if (method === "GET" && ttl > 0 && envelope.ok && this.canCache(ctx.cacheGeneration, key, `${path}${buildQueryString(opts.query)}`)) {
        this.cache.set(key, envelope, ttl);
        this.canonicalByKey.set(key, `${path}${buildQueryString(opts.query)}`);
      }
      if (method !== "GET" && envelope.ok) {
        const fragment = writeInvalidationFragment(path);
        if (fragment) this.invalidateMatching(fragment);
      }
      return this.notifyErrorEnvelope(envelope, method, path);
    } catch (err) {
      if (err instanceof RetryableHttpError) {
        return this.notifyErrorEnvelope(err.envelope, method, path);
      }
      return this.notifyErrorEnvelope(
        {
          statusCode: 0,
          ok: false,
          error: {
            message: err instanceof Error ? err.message : "Request aborted or network error",
            error_code: "EXT001"
          }
        },
        method,
        path
      );
    }
  }
  /** A single fetch attempt. Throws on retryable failures (5xx / network / timeout). */
  async attempt(url, method, headers, body, signal, timeoutMs, exposeHeaders, credentials, responseType) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    const onCallerAbort = () => ctrl.abort(signal?.reason);
    if (signal?.aborted) ctrl.abort(signal.reason);
    else signal?.addEventListener("abort", onCallerAbort, { once: true });
    const start = typeof performance !== "undefined" ? performance.now() : Date.now();
    try {
      const response = await fetch(url, {
        method,
        headers,
        body,
        signal: ctrl.signal,
        ...credentials ? { credentials } : {}
      });
      const durationMs = (typeof performance !== "undefined" ? performance.now() : Date.now()) - start;
      this.config.onResponse?.({ url, status: response.status, durationMs });
      if (!response.ok) {
        const envelope = toErrorEnvelope(await safeJson(response), response);
        if (isRetryableResponse(response.status, envelope.error)) {
          throw new RetryableHttpError(envelope, parseRetryAfterMs(response));
        }
        return envelope;
      }
      const exposed = pickHeaders(response, exposeHeaders);
      if (responseType === "text") {
        const text2 = await response.text();
        return { statusCode: response.status, ok: true, data: text2, ...exposed ? { headers: exposed } : {} };
      }
      const json = await safeJson(response);
      if (json && typeof json === "object" && "ok" in json) {
        const env = json;
        if (env.ok === true && env.data === void 0 && "result" in env) {
          env.data = env.result;
        }
        if (this.debugOn()) debugResourceSink.capture(url, json);
        if (exposed && json.ok) json.headers = { ...json.headers, ...exposed };
        return json;
      }
      return { statusCode: response.status, ok: true, data: json ?? void 0, ...exposed ? { headers: exposed } : {} };
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onCallerAbort);
    }
  }
  noteCacheInvalidation(match) {
    this.cacheInvalidations.push({ generation: ++this.cacheGeneration, ...match });
    if (this.cacheInvalidations.length > 200) this.cacheHistoryFloor = this.cacheInvalidations.shift().generation;
  }
  canCache(generation, key, canonical) {
    if (generation < this.cacheHistoryFloor) return false;
    return !this.cacheInvalidations.some((entry) => entry.generation > generation && (entry.canonical === canonical || entry.fragment !== void 0 && (key.includes(entry.fragment) || canonical.includes(entry.fragment))));
  }
  /** A navigation result belongs to this client/release, not the timed failover host. */
  navigationCacheKey(path, query) {
    return cacheKey("GET", `navigation:${this.resolveUrl(path, query, "GET", this.config.region)}`);
  }
  /** Read an unexpired GET synchronously for navigation restoration. */
  peek(path, query) {
    this.assertCanAccess();
    return this.cache.get(this.navigationCacheKey(path, query)) ?? this.cache.get(cacheKey("GET", this.resolveUrl(path, query)));
  }
  /** Capture an invalidation-safe writer before an explicit fresh read. */
  cacheWriter(path, query, ttlMs) {
    const generation = this.cacheGeneration;
    const key = this.navigationCacheKey(path, query);
    return (response) => {
      if (!response.ok || !this.canCache(generation, key, `${path}${buildQueryString(query)}`)) return;
      this.cache.set(key, response, ttlMs);
      this.canonicalByKey.set(key, `${path}${buildQueryString(query)}`);
    };
  }
  /** Invalidate a cached GET (call after a mutation that changes it). */
  invalidate(path, query) {
    this.noteCacheInvalidation({ canonical: `${path}${buildQueryString(query)}` });
    this.cache.delete(cacheKey("GET", this.resolveUrl(path, query)));
    this.cache.delete(this.navigationCacheKey(path, query));
    const canonical = `${path}${buildQueryString(query)}`;
    for (const [key, original] of this.canonicalByKey) {
      if (original === canonical) {
        this.cache.delete(key);
        this.canonicalByKey.delete(key);
      }
    }
  }
  /**
   * Invalidate every cached GET whose URL contains `fragment`.
   *
   * The exact-URL {@link invalidate} only reaches a read the caller can name in
   * full, query string included. That is rarely the read a write stales: one
   * resource is fetched at several scopes and with several sets of query params
   * by different surfaces, and the writer knows none of them. Without this, a
   * refetch issued straight after a successful write is served the PRE-write
   * body out of the 30s response cache, and the UI looks like the write was
   * dropped until the entry expires or the tab reloads.
   *
   * Matched against the resolved URL AND the canonical path the read was
   * requested with, so pass the same fragment the reads use (`"/addresses/"`,
   * `"/integration-versions"`). Matching the resolved URL alone missed every
   * service behind a rewrite: `/integration-versions` resolves to
   * `…/integrations-mgmt-api/blue/GET/US/v0/versions`, which contains neither
   * the fragment nor its rewrite (the verb and continent land mid-path) — so a
   * build list stayed stale after a deploy and a grant list after a grant.
   *
   * Falls back to clearing the whole cache when the configured store cannot
   * enumerate — over-invalidating costs a few refetches; under-invalidating
   * shows stale data as fact.
   */
  invalidateMatching(fragment) {
    const keys = this.cache.keys?.();
    this.noteCacheInvalidation({ fragment: keys ? fragment : "" });
    if (!keys) {
      this.cache.clear();
      this.canonicalByKey.clear();
      return;
    }
    const live = new Set(keys);
    for (const key of this.canonicalByKey.keys()) {
      if (!live.has(key)) this.canonicalByKey.delete(key);
    }
    for (const key of keys) {
      if (!key.startsWith("GET ")) continue;
      const canonical = this.canonicalByKey.get(key);
      if (key.includes(fragment) || canonical !== void 0 && canonical.includes(fragment)) {
        this.cache.delete(key);
        this.canonicalByKey.delete(key);
      }
    }
  }
  get(path, opts) {
    return this.request(path, { ...opts, method: "GET" });
  }
  post(path, body, opts) {
    return this.request(path, { ...opts, method: "POST", body });
  }
  patch(path, body, opts) {
    return this.request(path, { ...opts, method: "PATCH", body });
  }
  put(path, body, opts) {
    return this.request(path, { ...opts, method: "PUT", body });
  }
  delete(path, opts) {
    return this.request(path, { ...opts, method: "DELETE" });
  }
  /** Low-level escape hatch returning the unwrapped `.result` (throws on `ok:false`). */
  static unwrap = unwrapEnvelope;
};
async function safeJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
function toErrorEnvelope(parsed, response) {
  const fallbackMessage = response.statusText || "Request failed";
  if (parsed && typeof parsed === "object") {
    const body = parsed;
    const nested = body.error;
    if (nested && typeof nested === "object") {
      const err = nested;
      return {
        statusCode: response.status,
        ok: false,
        error: { ...err, message: err.message || fallbackMessage }
      };
    }
    const flat = body;
    const awsMessage = typeof body.Message === "string" ? body.Message : void 0;
    return {
      statusCode: response.status,
      ok: false,
      error: { ...flat, message: flat.message || awsMessage || fallbackMessage }
    };
  }
  return { statusCode: response.status, ok: false, error: { message: fallbackMessage } };
}
function pickHeaders(response, keys) {
  if (!keys || keys.length === 0) return void 0;
  const out = {};
  for (const key of keys) {
    const value = response.headers.get(key);
    if (value != null) out[key.toLowerCase()] = value;
  }
  return Object.keys(out).length > 0 ? out : void 0;
}

// src/api/errors-bridge.ts
var DEFAULT_ERROR_TYPE = "ApiError";
function errorEnvelopeToErrorBody(envelope) {
  const { error } = envelope;
  return {
    message: error.message,
    error_type: DEFAULT_ERROR_TYPE,
    error_code: errorCodeOf(error),
    error_level: error.error_level ?? null,
    details: error.details ?? {},
    correlation_id: error.correlation_id ?? null
  };
}
function errorEnvelopeToErrorResponse(envelope) {
  return {
    statusCode: envelope.statusCode,
    ok: false,
    error: errorEnvelopeToErrorBody(envelope)
  };
}
function thrownToErrorEnvelope(err) {
  const response = toErrorResponse(err);
  const { error } = response;
  return {
    statusCode: response.statusCode,
    ok: false,
    error: {
      message: error.message,
      ...error.error_code ? { error_code: error.error_code } : {},
      ...error.error_level ? { error_level: error.error_level } : {},
      ...error.details ? { details: error.details } : {},
      ...error.correlation_id ? { correlation_id: error.correlation_id } : {}
    }
  };
}

// src/api/outcome.ts
function readOutcome(found, error) {
  if (error !== void 0 && error !== null) {
    return error.statusCode === 404 ? "missing" : "failed";
  }
  return found ? "ready" : "failed";
}
function listOutcome(args) {
  if (args.count > 0) return "ready";
  if (args.loading) return "loading";
  return args.failed ? "failed" : "empty";
}

// src/transpose/transaction-row.ts
function unwrapTxEnvelope(entry) {
  const inner = entry?.transaction;
  return inner && typeof inner === "object" ? inner : entry ?? {};
}
function txEntryUniqueAddresses(entry) {
  const list = entry?.unique_addresses;
  return Array.isArray(list) ? list.filter((a) => typeof a === "string") : [];
}
function coerceTransactionRow(raw) {
  const tx = unwrapTxEnvelope(raw);
  const get = (...keys) => {
    for (const k of keys) {
      const v = tx[k];
      if (v != null && v !== "") return v;
    }
    return void 0;
  };
  const ps = tx.participant_summary ?? {};
  const body = tx.body ?? {};
  const nonEmpty = (v) => v != null && v !== "" ? String(v) : void 0;
  const chainId = String(get("chain_id", "chain") ?? "");
  const txHash = String(get("tx_hash", "transaction_hash", "hash") ?? "");
  const canonicalTxId = String(
    get("canonical_tx_id") ?? (chainId && txHash ? crossChainGroupingKey({ chainId, txHash }) : txHash)
  );
  const tsRaw = get("block_timestamp", "timestamp");
  const blockTimestamp = typeof tsRaw === "number" ? tsRaw : Number(tsRaw ?? 0) || 0;
  return {
    ...tx,
    canonical_tx_id: canonicalTxId,
    chain_id: chainId,
    tx_hash: txHash,
    block_timestamp: blockTimestamp,
    total_value_raw: String(get("total_value_raw", "value") ?? "0"),
    type: get("type") ?? "transaction",
    finality: get("finality") ?? "unknown",
    from: get("from", "from_address") ?? nonEmpty(ps.primary_from) ?? nonEmpty(body.from_address) ?? null,
    to: get("to", "to_address") ?? nonEmpty(ps.primary_to) ?? nonEmpty(body.to_address) ?? null,
    primary_entity_id: get("primary_entity_id") ?? null
    // The feed/list projection is a small subset of the generated row contract
    // (which marks the tenant-scoping keys required); the producer fields ride
    // along on `...tx`. Cast through `unknown` — the shape is intentionally partial.
  };
}
function coerceTransactionRows(raws) {
  return raws.map(coerceTransactionRow);
}

// src/transpose/addresses.ts
function counterIndex(graph) {
  const index = /* @__PURE__ */ new Map();
  if (!graph) return index;
  for (const node of graph.nodes ?? []) {
    if (node && typeof node.address === "string") index.set(node.address, node);
  }
  return index;
}
function toUniqueAddresses(addresses, options = {}) {
  const counters = counterIndex(options.graph);
  const out = [];
  for (const address of addresses) {
    if (!address) continue;
    const node = counters.get(address);
    const overlay = options.resolveOverlay?.(address);
    out.push({
      address,
      chainId: options.chainId,
      degree: node?.degree,
      txCount: node?.tx_count,
      label: overlay?.label,
      primaryEntityId: overlay?.primaryEntityId,
      tags: overlay?.tags,
      color: overlay?.color,
      logo: overlay?.logo
    });
  }
  return out;
}

// src/transpose/flows.ts
function tokenPriceFor(prices, tokenAddress, chainId) {
  if (!tokenAddress || !prices) return void 0;
  return prices[normalizeAddress(tokenAddress, chainId)] ?? prices[tokenAddress.toLowerCase()];
}
function priceFor(tokenAddress, prices) {
  return tokenPriceFor(prices, tokenAddress);
}
function resolveSymbol(tokenAddress, kind, price, options) {
  const explicit = options.resolveSymbol?.(tokenAddress);
  if (explicit) return explicit;
  if (price?.symbol) return price.symbol;
  if (!tokenAddress || !TOKEN_FLOW_KINDS.has(kind)) return options.nativeSymbol;
  return void 0;
}
function toTokenFlowRows(flows, options = {}) {
  return flows.map((flow) => {
    const price = priceFor(flow.token_address, options.prices);
    const decimals = flow.decimals ?? price?.decimals ?? void 0;
    const symbol = resolveSymbol(flow.token_address, flow.kind, price, options);
    const amountDisplay = decimals != null ? formatTokenDisplay(flow.raw_amount, decimals) : void 0;
    let valueUsd = flow.value_usd ?? void 0;
    if (valueUsd == null) {
      const unitPrice = flow.price_usd ?? price?.price_usd ?? void 0;
      if (decimals != null && unitPrice != null) {
        valueUsd = tokenToUsd(flow.raw_amount, decimals, unitPrice);
      }
    }
    return {
      kind: flow.kind,
      tokenAddress: flow.token_address ?? void 0,
      symbol,
      from: flow.from_address ?? void 0,
      to: flow.to_address ?? void 0,
      rawAmount: flow.raw_amount,
      amountDisplay,
      valueUsd,
      valueUsdDisplay: valueUsd != null ? formatUsd(valueUsd) : void 0,
      logIndex: flow.log_index ?? void 0
    };
  });
}
function directionOf(rawDelta) {
  try {
    const v = BigInt(rawDelta);
    if (v > 0n) return "in";
    if (v < 0n) return "out";
    return "flat";
  } catch {
    return "flat";
  }
}
function toNetFlowRows(netFlows, options = {}) {
  return netFlows.map((net) => {
    const price = priceFor(net.token_address, options.prices);
    const decimals = price?.decimals ?? void 0;
    const symbol = resolveSymbol(net.token_address, net.token_address ? "erc20" : "native", price, options);
    const deltaDisplay = decimals != null ? formatTokenDisplay(net.raw_delta, decimals) : void 0;
    const valueUsd = net.value_usd ?? void 0;
    return {
      address: net.address,
      tokenAddress: net.token_address ?? void 0,
      symbol,
      rawDelta: net.raw_delta,
      deltaDisplay,
      valueUsd,
      valueUsdDisplay: valueUsd != null ? formatUsd(valueUsd) : void 0,
      direction: directionOf(net.raw_delta)
    };
  });
}

// src/transpose/graph.ts
function nodeView(node, options) {
  const chainId = node.chain_id ?? options.chainId;
  const overlay = node.address ? options.resolveOverlay?.(node.address, chainId) : void 0;
  const id = node.id || node.address;
  const fallbackLabel = node.address ? shortenAddress(node.address) : id;
  return {
    id,
    address: node.address,
    degree: node.degree ?? 0,
    txCount: node.tx_count ?? 0,
    receiptProtocolIds: node.receipt_protocol_ids ?? void 0,
    // `?? undefined` and not `?? 0`: hop 0 is the SEED, so a default of 0 would
    // make every node of a graph that carries no hops look like the subject.
    ...typeof node.hop === "number" ? { hop: node.hop } : {},
    label: overlay?.label ?? node.label ?? fallbackLabel,
    chainId,
    nodeType: node.node_type ?? void 0,
    // Overlay brand/attribution (undefined when the address carries none).
    logo: overlay?.logo,
    color: overlay?.color,
    primaryEntityId: overlay?.primaryEntityId,
    tags: overlay?.tags && overlay.tags.length > 0 ? overlay.tags : void 0,
    // The overlay's weight, not only its name — the same pair the scene
    // transpose carries. A node whose label IS the accusation has to be able to
    // look like one, and `GraphNode`'s risk badge had nothing to read here.
    riskLevel: overlay?.riskLevel,
    riskScore: overlay?.riskScore
  };
}
function endpoint(id, address) {
  return id || address;
}
function graphEdgeId(edge) {
  const token = edge.token_address ?? NATIVE_TOKEN;
  const kind = edge.kind ?? "";
  const from = endpoint(edge.from_id, edge.from_address);
  const to = endpoint(edge.to_id, edge.to_address);
  return `${from}->${to}:${token}:${kind}`;
}
function nativeAssetOf(edge, chainId) {
  if (edge.token_address) return null;
  if (!NATIVE_COIN_FLOW_KINDS.has(edge.kind ?? "")) return null;
  if (!getChainMetadata(chainId)) return null;
  const meta = nativeTokenMeta(chainId);
  if (typeof meta.decimals !== "number") return null;
  return { decimals: meta.decimals, ...meta.symbol ? { symbol: meta.symbol } : {} };
}
function edgeView(edge, options) {
  const assetChain = edge.token_chain_id ?? (edge.kind === "bridge_link" ? edge.to_chain : edge.from_chain) ?? options.chainId;
  const price = tokenPriceFor(options.prices, edge.token_address, assetChain);
  const native = nativeAssetOf(edge, assetChain);
  const decimals = edge.decimals ?? price?.decimals ?? native?.decimals ?? void 0;
  const symbol = options.resolveSymbol?.(edge.token_address) ?? price?.symbol ?? native?.symbol ?? void 0;
  const amountDisplay = decimals != null ? formatTokenDisplay(edge.raw_amount_total, decimals) : void 0;
  const valueUsd = decimals != null && price?.price_usd != null ? tokenToUsd(edge.raw_amount_total, decimals, price.price_usd) : void 0;
  const fromChain = edge.from_chain ?? void 0;
  const toChain = edge.to_chain ?? void 0;
  return {
    id: graphEdgeId(edge),
    from: endpoint(edge.from_id, edge.from_address),
    to: endpoint(edge.to_id, edge.to_address),
    tokenAddress: edge.token_address ?? void 0,
    kind: edge.kind ?? void 0,
    interactionCount: edge.interaction_count ?? 0,
    rawAmountTotal: edge.raw_amount_total,
    assetChain: assetChain ?? void 0,
    amountExact: decimals != null ? formatUnits(edge.raw_amount_total, decimals) : void 0,
    amountDisplay,
    symbol,
    valueUsd,
    fromChain,
    toChain,
    crossChain: Boolean(fromChain && toChain && fromChain !== toChain)
  };
}
function toGraphView(graph, options = {}) {
  const nodes = (graph.nodes ?? []).map((n) => nodeView(n, options));
  const edges = (graph.edges ?? []).map((e) => edgeView(e, options));
  return { nodes, edges };
}
function totalEdgeUsd(view) {
  let total = 0;
  for (const edge of view.edges) if (edge.valueUsd != null) total += edge.valueUsd;
  return total;
}
function totalEdgeUsdDisplay(view) {
  return formatUsd(totalEdgeUsd(view), { compact: true });
}

// src/transpose/scene.ts
function mergeTags(backend, overlay) {
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  const push = (t) => {
    if (t && !seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  };
  if (Array.isArray(backend)) backend.forEach((t) => push(String(t)));
  (overlay ?? []).forEach(push);
  return out.length > 0 ? out : void 0;
}
function joinCdn(base, key) {
  return `${base.replace(/\/+$/, "")}/${key.replace(/^\/+/, "")}`;
}
var SLUG = /^[a-z0-9]+(?:[_-][a-z0-9]+)*$/;
function iconName(alt) {
  const value = (alt ?? "").trim();
  if (!value) return void 0;
  const named = protocolDisplayName(value) ?? protocolFamilyName(value);
  if (named) return named;
  if (!SLUG.test(value)) return value;
  return value.split(/[_-]/).map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}
function slugAsName(sublabel, protocolName, iconAlt) {
  const value = (sublabel ?? "").trim();
  if (!value) return sublabel ?? void 0;
  if (!SLUG.test(value) || (iconAlt ?? "").trim() !== value) return value;
  return protocolDisplayName(value) ?? protocolName ?? protocolFamilyName(value) ?? value;
}
function namedIcon(icon, rawAlt, protocolName) {
  if (!icon || !protocolName) return icon;
  const raw = (rawAlt ?? "").trim();
  return raw && SLUG.test(raw) && !protocolDisplayName(raw) ? { ...icon, alt: protocolName } : icon;
}
function resolveMediaRef(ref, options = {}) {
  if (!ref || !ref.value) return void 0;
  const alt = iconName(ref.alt);
  const fallbackSlug = ref.fallback ?? void 0;
  switch (ref.kind) {
    case "url":
      return { url: ref.value, alt, slug: fallbackSlug };
    case "cdn_key": {
      if (options.cdnBase) {
        return { url: joinCdn(options.cdnBase, ref.value), slug: fallbackSlug, alt };
      }
      const url = fallbackSlug ? options.resolvePlaceholder?.(fallbackSlug) : void 0;
      return { url, slug: url ? void 0 : fallbackSlug, alt };
    }
    case "placeholder":
    default: {
      const url = options.resolvePlaceholder?.(ref.value);
      return { url, slug: url ? void 0 : ref.value, alt };
    }
  }
}
function scaledAmount(amount, raw, decimals) {
  return amount ?? (raw != null && decimals != null ? formatUnits(raw, decimals) : void 0);
}
function assetLabel(asset) {
  if (!asset) return void 0;
  const amount = scaledAmount(asset.amount, asset.amount_raw, asset.decimals) ?? (asset.amount_raw != null ? `${asset.amount_raw} base units` : void 0);
  const parts = [amount, titledSymbol(asset.symbol, asset.impersonates)].filter(Boolean);
  if (!parts.length) return void 0;
  const label = parts.join(" ");
  return asset.basis === AMOUNT_BASIS.QUOTED_MINIMUM ? `${label} (min)` : label;
}
function parseUsd(usd) {
  if (usd == null) return void 0;
  const n = Number.parseFloat(usd);
  return Number.isFinite(n) ? n : void 0;
}
function pointOf(value) {
  if (typeof value !== "object" || value === null) return void 0;
  const { x, y } = value;
  if (typeof x !== "number" || typeof y !== "number") return void 0;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return void 0;
  return { x, y };
}
function isFailed(attributes) {
  if (!attributes) return false;
  const status = String(attributes.status ?? "").toLowerCase();
  if (status === "failed" || status === "reverted" || status === "error" || status === "revert") {
    return true;
  }
  if (attributes.failed === true || attributes.reverted === true) return true;
  if (attributes.success === false) return true;
  return false;
}
function assetViews(assets, options) {
  return (assets ?? []).filter((a) => a.symbol || a.token_address).map((a) => ({
    tokenAddress: a.token_address ?? void 0,
    tokenChainId: a.token_chain_id ?? void 0,
    symbol: a.symbol ?? void 0,
    decimals: a.decimals ?? void 0,
    amount: a.amount ?? void 0,
    amountRaw: a.amount_raw ?? void 0,
    usd: a.usd ?? void 0,
    direction: a.direction ?? void 0,
    basis: a.basis ?? void 0,
    minAmount: a.min_amount ?? void 0,
    maxAmount: a.max_amount ?? void 0,
    icon: resolveMediaRef(a.icon, options),
    impersonates: parseTokenImpersonation(a.impersonates)
  }));
}
function sourceRefViews(refs) {
  return (refs ?? []).filter((r) => r.ref_type).map((r) => ({
    refType: r.ref_type,
    address: r.address ?? void 0,
    logIndex: r.log_index ?? void 0,
    internalTxIndex: r.internal_tx_index ?? void 0,
    path: r.path ?? []
  }));
}
function factViews(facts) {
  return (facts ?? []).filter((f) => f.key && f.value !== null && f.value !== void 0 && f.value !== "").map((f) => ({
    key: f.key,
    label: f.label || f.key,
    kind: f.kind ?? void 0,
    value: f.value,
    protocolId: f.protocol_id ?? void 0,
    confidence: f.confidence ?? void 0,
    evidence: f.evidence?.length ? f.evidence : void 0,
    sourceRefs: f.source_refs?.length ? sourceRefViews(f.source_refs) : void 0,
    semanticNodeId: f.semantic_node_id ?? void 0,
    pathId: f.path_id ?? void 0,
    description: f.description ?? void 0
  }));
}
function elementOrigin(origin, legacyVirtual) {
  if (origin && origin !== "decoded") return origin;
  return legacyVirtual ? "synthesized" : "decoded";
}
function nodeView2(node, options) {
  const address = node.address ?? void 0;
  const short = address ? shortenAddress(address) : void 0;
  const overlay = address ? options.resolveOverlay?.(address, node.chain_id ?? void 0) : void 0;
  const baseLabel = node.label && node.label !== address ? node.label : short ?? (node.label || shortenCompositeIdentifier(node.id));
  const idLower = node.id.toLowerCase();
  const addressLower = address?.toLowerCase();
  const isTheAddressItself = addressLower != null && (idLower === addressLower || idLower.endsWith(`#${addressLower}`) || node.label?.toLowerCase() === addressLower);
  const label = (isTheAddressItself ? overlay?.label : void 0) ?? baseLabel;
  const identity = address && label !== short ? short : void 0;
  const protocolName = typeof node.attributes?.protocol === "string" && !SLUG.test(node.attributes.protocol.trim()) ? node.attributes.protocol.trim() : void 0;
  const said = slugAsName(node.sublabel, protocolName, node.icon?.alt);
  const sublabel = identity ? [identity, said].filter(Boolean).join(" \xB7 ") : said ?? void 0;
  const overlayIcon = overlay?.logo ? { url: overlay.logo, alt: overlay.label ?? label } : void 0;
  const origin = elementOrigin(node.origin, node.attributes?.virtual === true);
  const pinned = origin === "user" ? pointOf(node.attributes?.position) : void 0;
  return {
    id: node.id,
    label,
    sublabel: sublabel ?? void 0,
    chainId: node.chain_id ?? void 0,
    address,
    kind: node.kind,
    offChain: node.off_chain,
    protocol: typeof node.attributes?.protocol === "string" ? node.attributes.protocol : void 0,
    receiptProtocolIds: Array.isArray(node.attributes?.receipt_protocol_ids) ? node.attributes.receipt_protocol_ids.filter((id) => typeof id === "string") : void 0,
    groupId: node.group_id ?? void 0,
    entityId: node.entity_id ?? overlay?.primaryEntityId,
    icon: overlayIcon ?? namedIcon(resolveMediaRef(node.icon, options), node.icon?.alt, protocolName),
    value: node.value ?? void 0,
    badge: node.badge ?? void 0,
    confidence: node.confidence,
    evidence: node.evidence,
    tags: mergeTags(node.attributes?.tags, overlay?.tags),
    // The overlay's own weight, not only its name. Without it a node labelled
    // "Fake_Phishing7575952" renders exactly like one labelled "Uniswap:
    // Router" — same chip, same colour, no badge.
    riskLevel: overlay?.riskLevel,
    riskScore: overlay?.riskScore,
    failed: isFailed(node.attributes),
    contested: claimViews(node.attributes),
    position: pinned,
    virtual: origin === "synthesized",
    origin,
    facts: factViews(node.facts),
    assets: assetViews(node.assets, options),
    sourceRefs: sourceRefViews(node.source_refs),
    detailTier: node.detail_tier ?? "compact"
  };
}
function edgeTransfers(edge) {
  const refs = mergedTxRefs(edge.attributes);
  if (refs.length === 0) return void 0;
  const decimals = edge.asset?.decimals;
  return refs.map((ref) => ({
    id: ref.id,
    label: shortenHash(ref.tx_hash ?? ref.id),
    ...ref.amount_raw != null ? { amount: scaledAmount(void 0, ref.amount_raw, decimals), amountRaw: ref.amount_raw } : {}
  }));
}
function edgeView2(edge, options, bidirectional) {
  const origin = elementOrigin(edge.origin, edge.virtual);
  const synthesized = origin === "synthesized";
  return {
    id: edge.id,
    from: edge.source,
    to: edge.target,
    kind: edge.kind,
    // A wash is worded, not counted. The backend's label is the arithmetic
    // ("0 USDC"), which on a line between two addresses reads as "nothing
    // happened here" — the opposite of what happened.
    label: edge.attributes?.net_zero === true ? `nets to zero${edge.asset?.symbol ? ` \xB7 ${edge.asset.symbol}` : ""}` : edge.label ?? assetLabel(edge.asset),
    crossChain: edge.cross_chain,
    virtual: synthesized,
    origin,
    facts: factViews(edge.facts),
    dashed: synthesized,
    amount: scaledAmount(edge.asset?.amount, edge.asset?.amount_raw, edge.asset?.decimals),
    amountRaw: edge.asset?.amount_raw ?? void 0,
    symbol: edge.asset?.symbol ?? void 0,
    tokenAddress: edge.asset?.token_address ?? void 0,
    valueUsd: parseUsd(edge.asset?.usd),
    basis: edge.asset?.basis ?? void 0,
    minAmount: scaledAmount(edge.asset?.min_amount, edge.asset?.min_amount_raw, edge.asset?.decimals),
    minAmountRaw: edge.asset?.min_amount_raw ?? void 0,
    maxAmount: scaledAmount(edge.asset?.max_amount, edge.asset?.max_amount_raw, edge.asset?.decimals),
    maxAmountRaw: edge.asset?.max_amount_raw ?? void 0,
    direction: edge.direction ?? edge.asset?.direction ?? void 0,
    bidirectional: bidirectional.has(unorderedPairKey(edge.source, edge.target)),
    netted: edge.attributes?.netted === true,
    contested: claimViews(edge.attributes),
    mergedCount: mergedCount(edge.attributes) > 1 ? mergedCount(edge.attributes) : void 0,
    transfers: edgeTransfers(edge),
    compressedVia: Array.isArray(edge.attributes?.compressed_via) ? edge.attributes.compressed_via.filter(
      (v) => typeof v === "string"
    ) : void 0,
    netZero: edge.attributes?.net_zero === true,
    failed: isFailed(edge.attributes),
    icon: resolveMediaRef(edge.icon, options),
    fromChain: edge.from_chain ?? void 0,
    toChain: edge.to_chain ?? void 0,
    detailTier: edge.detail_tier ?? "compact"
  };
}
function claimViews(attributes) {
  const raw = attributes?.[CONTESTED_ATTR];
  if (!Array.isArray(raw) || raw.length === 0) return void 0;
  const out = raw.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) return [];
    const { field, value, by, scope } = entry;
    if (typeof field !== "string") return [];
    return [
      {
        field,
        value: typeof value === "string" ? value : JSON.stringify(value ?? null),
        by: typeof by === "string" && by ? by : void 0,
        scope: typeof scope === "string" && scope ? scope : void 0
      }
    ];
  });
  return out.length > 0 ? out : void 0;
}
function unorderedPairKey(a, b) {
  return a < b ? `${a}\0${b}` : `${b}\0${a}`;
}
function bidirectionalPairs(edges) {
  const directed = /* @__PURE__ */ new Set();
  const both = /* @__PURE__ */ new Set();
  for (const edge of edges) {
    if (edge.source === edge.target) continue;
    directed.add(`${edge.source}\0${edge.target}`);
    if (directed.has(`${edge.target}\0${edge.source}`)) {
      both.add(unorderedPairKey(edge.source, edge.target));
    }
  }
  return both;
}
function groupView(group, options) {
  const colors = getChainGroupColors(group.chain_id);
  const label = (group.chain_id ? options.chainLabel?.(group.chain_id) : void 0) ?? (group.label && group.chain_id !== group.label ? group.label : colors.name);
  const tint = group.color ? brandGroupTint(group.color) : colors;
  return {
    id: group.id,
    groupType: group.group_type,
    chainId: group.chain_id ?? void 0,
    label,
    memberNodeIds: group.member_node_ids,
    fill: tint.fill,
    border: tint.border,
    text: tint.text,
    brandColor: group.color ?? colors.brandColor
  };
}
function isAddressAbbreviation(symbol, address) {
  const [head, tail, ...rest] = symbol.split("\u2026");
  if (rest.length > 0 || !head || !tail) return false;
  return symbol.length < address.length && address.startsWith(head) && address.endsWith(tail);
}
function sceneTokenSymbols(scenes) {
  const symbols = /* @__PURE__ */ new Map();
  const add = (asset) => {
    const address = asset?.token_address;
    const symbol = asset?.symbol?.trim();
    if (!address || !symbol || symbols.has(address)) return;
    if (isAddressAbbreviation(symbol, address)) return;
    symbols.set(address, symbol);
  };
  for (const scene of scenes) {
    for (const node of scene?.nodes ?? []) for (const asset of node.assets ?? []) add(asset);
    for (const edge of scene?.edges ?? []) add(edge.asset);
  }
  return symbols;
}
function sceneTokenDecimals(scenes) {
  const decimals = /* @__PURE__ */ new Map();
  const add = (asset) => {
    const address = asset?.token_address;
    const value = asset?.decimals;
    if (!address || decimals.has(address)) return;
    if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value > 36) return;
    decimals.set(address, value);
  };
  for (const scene of scenes) {
    for (const node of scene?.nodes ?? []) for (const asset of node.assets ?? []) add(asset);
    for (const edge of scene?.edges ?? []) add(edge.asset);
  }
  return decimals;
}
function toSceneView(scene, options = {}) {
  const edges = scene.edges ?? [];
  const bidirectional = bidirectionalPairs(edges);
  return {
    kind: scene.kind,
    summary: scene.summary ?? void 0,
    nodes: (scene.nodes ?? []).map((n) => nodeView2(n, options)),
    edges: edges.map((e) => edgeView2(e, options, bidirectional)),
    groups: (scene.groups ?? []).map((g) => groupView(g, options)),
    chainIds: scene.chain_ids ?? [],
    multiChain: scene.multi_chain,
    layoutHint: scene.layout_hint,
    defaultDetailTier: scene.default_detail_tier ?? "compact",
    // Only meaningful on an empty scene; the backend only stamps it there.
    emptyReason: typeof scene.attributes?.[SCENE_EMPTY_REASON_ATTR] === "string" ? scene.attributes[SCENE_EMPTY_REASON_ATTR] : void 0
  };
}

// src/transpose/detail.ts
var DETAIL_TIER_ORDER = SCENE_DETAIL_TIERS;
function detailTierRank(tier) {
  const i = DETAIL_TIER_ORDER.indexOf(tier ?? "compact");
  return i < 0 ? 0 : i;
}
function detailTierVisible(elementTier, viewTier) {
  return detailTierRank(elementTier) <= detailTierRank(viewTier);
}
function maxDetailTier(a, b) {
  return DETAIL_TIER_ORDER[Math.max(detailTierRank(a), detailTierRank(b))];
}
function containsChildren(edges) {
  const out = /* @__PURE__ */ new Map();
  for (const e of edges) {
    if (e.kind !== "contains") continue;
    const kids = out.get(e.from);
    if (kids) kids.push(e.to);
    else out.set(e.from, [e.to]);
  }
  return out;
}
function isAlwaysVisible(node) {
  return node.origin === "user";
}
function visibleNodeIds(view, tier, expanded) {
  const visible = /* @__PURE__ */ new Set();
  for (const n of view.nodes) {
    if (isAlwaysVisible(n) || detailTierVisible(n.detailTier, tier)) visible.add(n.id);
  }
  if (expanded.size === 0) return visible;
  const children = containsChildren(view.edges);
  const seen = /* @__PURE__ */ new Set();
  const stack = [];
  for (const id of expanded) if (visible.has(id)) stack.push(id);
  while (stack.length) {
    const id = stack.pop();
    for (const child of children.get(id) ?? []) {
      if (!visible.has(child)) visible.add(child);
      if (expanded.has(child) && !seen.has(child)) {
        seen.add(child);
        stack.push(child);
      }
    }
  }
  return visible;
}
function filterSceneViewByDetailTier(view, tier, options = {}) {
  const visible = visibleNodeIds(view, tier, new Set(options.expandedNodeIds ?? []));
  const nodes = view.nodes.filter((n) => visible.has(n.id));
  const edges = view.edges.filter((e) => visible.has(e.from) && visible.has(e.to));
  const anchor = visibleAnchor(view.edges, visible);
  edges.push(...liftedEdges(view.edges, visible, edges, anchor));
  edges.push(...relayedEdges(view, visible, edges, anchor));
  const groups = view.groups.map((g) => ({ ...g, memberNodeIds: g.memberNodeIds.filter((id) => visible.has(id)) })).filter((g) => g.memberNodeIds.length > 0);
  return { ...view, nodes, edges, groups };
}
function visibleAnchor(all, visible) {
  const parentOf = /* @__PURE__ */ new Map();
  for (const e of all) if (e.kind === "contains" && !parentOf.has(e.to)) parentOf.set(e.to, e.from);
  return (id) => {
    const seen = /* @__PURE__ */ new Set();
    let current = id;
    while (current !== void 0 && !visible.has(current)) {
      if (seen.has(current)) return null;
      seen.add(current);
      current = parentOf.get(current);
    }
    return current ?? null;
  };
}
var pairKey = (a, b) => a < b ? `${a}\0${b}` : `${b}\0${a}`;
function liftedEdges(all, visible, drawn, anchor) {
  const joined = new Set(drawn.map((e) => pairKey(e.from, e.to)));
  const out = [];
  for (const e of all) {
    if (e.kind === "contains" || visible.has(e.from) && visible.has(e.to)) continue;
    const from = anchor(e.from);
    const to = anchor(e.to);
    if (!from || !to || from === to || joined.has(pairKey(from, to))) continue;
    joined.add(pairKey(from, to));
    out.push({ ...e, from, to });
  }
  return out;
}
function relayedEdges(view, visible, drawn, anchor) {
  const nodeById = new Map(view.nodes.map((n) => [n.id, n]));
  const isRelay = (id) => !visible.has(id) && nodeById.get(id)?.offChain === true && anchor(id) === null;
  const outgoing = /* @__PURE__ */ new Map();
  for (const e of view.edges) {
    if (e.kind === "contains") continue;
    const list = outgoing.get(e.from);
    if (list) list.push(e);
    else outgoing.set(e.from, [e]);
  }
  const network = /* @__PURE__ */ new Map();
  const root = (id) => {
    let r = id;
    while (network.get(r) !== r) r = network.get(r);
    return r;
  };
  for (const n of view.nodes) if (isRelay(n.id)) network.set(n.id, n.id);
  for (const e of view.edges) {
    if (e.kind !== "contains" && network.has(e.from) && network.has(e.to)) {
      network.set(root(e.from), root(e.to));
    }
  }
  const ends = /* @__PURE__ */ new Map();
  const endsOf = (relay) => {
    const key = root(relay);
    let entry = ends.get(key);
    if (!entry) ends.set(key, entry = { in: /* @__PURE__ */ new Set(), out: /* @__PURE__ */ new Set() });
    return entry;
  };
  for (const e of view.edges) {
    if (e.kind === "contains") continue;
    if (network.has(e.to) && !network.has(e.from)) {
      const end = anchor(e.from);
      if (end) endsOf(e.to).in.add(end);
    }
    if (network.has(e.from) && !network.has(e.to)) {
      const end = anchor(e.to);
      if (end) endsOf(e.from).out.add(end);
    }
  }
  const unambiguous = (relay) => {
    const end = endsOf(relay);
    return end.in.size <= 1 || end.out.size <= 1;
  };
  const joined = new Set(drawn.map((e) => pairKey(e.from, e.to)));
  const out = [];
  for (const first of view.edges) {
    if (first.kind === "contains" || !isRelay(first.to) || isRelay(first.from)) continue;
    if (!unambiguous(first.to)) continue;
    const from = anchor(first.from);
    if (!from) continue;
    const stack = [[first]];
    const seen = /* @__PURE__ */ new Set([first.to]);
    while (stack.length) {
      const path = stack.pop();
      for (const next of outgoing.get(path[path.length - 1].to) ?? []) {
        if (isRelay(next.to)) {
          if (!seen.has(next.to)) {
            seen.add(next.to);
            stack.push([...path, next]);
          }
          continue;
        }
        const to = anchor(next.to);
        if (!to || to === from || joined.has(pairKey(from, to))) continue;
        joined.add(pairKey(from, to));
        out.push(relayedEdge(from, to, [...path, next], nodeById));
      }
    }
  }
  return out;
}
function relayedEdge(from, to, path, nodeById) {
  const first = path[0];
  const last = path[path.length - 1];
  const carried = [...path].reverse().find((e) => e.amount !== void 0 || e.amountRaw !== void 0) ?? last;
  const networks = [...new Set(path.slice(1).map((e) => nodeById.get(e.from)?.label).filter(Boolean))];
  const fromChain = nodeById.get(from)?.chainId;
  const toChain = nodeById.get(to)?.chainId;
  const crossChain = path.some((e) => e.crossChain) || !!fromChain && !!toChain && fromChain !== toChain;
  const facts = [...new Map(path.flatMap((e) => e.facts).map((f) => [JSON.stringify(f), f])).values()];
  return {
    ...carried,
    id: `relayed:${first.id}|${last.id}`,
    from,
    to,
    kind: crossChain ? "cross_chain" : last.kind,
    label: networks.length > 0 ? `via ${networks.join(" \u2192 ")}` : carried.label,
    crossChain,
    virtual: true,
    dashed: true,
    origin: "synthesized",
    facts
  };
}
function nodesWithHiddenChildren(view, tier, options = {}) {
  const children = containsChildren(view.edges);
  if (children.size === 0) return /* @__PURE__ */ new Set();
  const expanded = new Set(options.expandedNodeIds ?? []);
  const visible = visibleNodeIds(view, tier, expanded);
  const out = /* @__PURE__ */ new Set();
  for (const [parent, kids] of children) {
    if (!visible.has(parent) || expanded.has(parent)) continue;
    for (const child of kids) {
      if (!visible.has(child)) {
        out.add(parent);
        break;
      }
    }
  }
  return out;
}

// src/transpose/assignment.ts
function hasAssignmentActions(handlers) {
  if (!handlers) return false;
  return Boolean(
    handlers.onSetLabel || handlers.onAddTag || handlers.onRemoveTag || handlers.onAddGroup || handlers.onRemoveGroup || handlers.onAssignEntity || handlers.onClearEntity
  );
}
function mergeAssignmentState(prev, change) {
  const next = { ...prev };
  if (change.label !== void 0) next.label = change.label;
  if (change.entityId !== void 0) next.entityId = change.entityId;
  if (change.entityLabel !== void 0) next.entityLabel = change.entityLabel;
  if (change.tags !== void 0) next.tags = [...change.tags];
  if (change.groups !== void 0) next.groups = [...change.groups];
  if (change.addTag) next.tags = withMember(next.tags, change.addTag);
  if (change.removeTag) next.tags = withoutMember(next.tags, change.removeTag);
  if (change.addGroup) next.groups = withMember(next.groups, change.addGroup);
  if (change.removeGroup) next.groups = withoutMember(next.groups, change.removeGroup);
  return next;
}
function withMember(current, member) {
  const members = new Set(current ?? []);
  members.add(member);
  return [...members];
}
function withoutMember(current, member) {
  return (current ?? []).filter((m) => m !== member);
}

// src/api/transaction-search.ts
function transactionSearchCursor(payload) {
  if (!isRecord(payload)) return null;
  const pagination = isRecord(payload.pagination) ? payload.pagination : null;
  const next = pagination?.next ?? payload.next_cursor;
  return typeof next === "string" && next ? next : null;
}
async function searchTransactionPage(client, options) {
  if (options.terms.length === 0) return { rows: [], nextCursor: null };
  const payload = unwrapData(await client.post("/transactions/search", {
    terms: options.terms,
    limit: options.limit,
    ...options.cursor ? { cursor: options.cursor } : {},
    ...typeof options.since === "number" && options.since > 0 ? { since: options.since } : {},
    ...typeof options.until === "number" && options.until > 0 ? { until: options.until } : {}
  }, { query: options.scopeParams ?? {}, signal: options.signal }));
  return { rows: coerceTransactionRows(rowsFromWire(payload)), nextCursor: transactionSearchCursor(payload) };
}
var text = (value) => typeof value === "string" && value.trim() ? value.trim() : void 0;
var unit = (value) => typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1 ? value : void 0;
var seconds = (value) => typeof value === "number" && Number.isFinite(value) && value > 0 ? value : void 0;
var strings = (value) => Array.isArray(value) ? value.flatMap((item) => text(item) ? [text(item)] : []) : [];
var httpUrl = (value) => {
  const raw = text(value);
  if (!raw) return void 0;
  try {
    const url = new URL(raw);
    return url.protocol === "https:" || url.protocol === "http:" ? url.toString() : void 0;
  } catch {
    return void 0;
  }
};
var evidenceSchema = z.object({
  source: z.object({ kind: z.unknown(), id: z.unknown(), url: z.unknown() }).partial().passthrough().nullish(),
  confidence: z.unknown(),
  reasons: z.unknown(),
  signals: z.unknown(),
  asserted_fields: z.unknown(),
  asserted_at: z.unknown(),
  last_seen_at: z.unknown()
}).partial().passthrough();
function parseEvidence(raw) {
  const parsed = evidenceSchema.safeParse(raw);
  if (!parsed.success) return void 0;
  const { source, confidence, reasons, signals, asserted_fields, asserted_at, last_seen_at } = parsed.data;
  const sourceKind = text(source?.kind);
  const sourceId = text(source?.id);
  const url = httpUrl(source?.url);
  const signalNames = signals && typeof signals === "object" && !Array.isArray(signals) ? Object.keys(signals) : strings(signals);
  return {
    ...sourceKind ? { sourceKind } : {},
    ...sourceId ? { sourceId } : {},
    ...url ? { url } : {},
    ...unit(confidence) !== void 0 ? { confidence: unit(confidence) } : {},
    reasons: strings(reasons),
    signals: signalNames,
    assertedFields: strings(asserted_fields),
    ...seconds(asserted_at) ? { assertedAt: seconds(asserted_at) } : {},
    ...seconds(last_seen_at) ? { lastSeenAt: seconds(last_seen_at) } : {}
  };
}
function parseTokenFacts(raw) {
  const row = raw && typeof raw === "object" ? raw : {};
  const category = text(row.category)?.toLowerCase();
  const sources = (text(row.source) ?? "").split(",").map((part) => part.trim()).filter(Boolean);
  const score = row.security_score;
  const scored = row.confidence_scored;
  return {
    ...category && category !== "unknown" ? { category } : {},
    providerCategories: strings(row.categories),
    ...text(row.logo_uri) ? { logoUri: text(row.logo_uri) } : {},
    isVerified: row.is_verified === true,
    ...typeof score === "number" && Number.isInteger(score) && score >= 0 && score <= 100 ? { securityScore: score } : {},
    sources,
    ...unit(row.confidence) !== void 0 ? { confidence: unit(row.confidence) } : {},
    ...typeof scored === "boolean" || scored === null ? { confidenceScored: scored } : {},
    ...text(row.lifecycle) ? { lifecycle: text(row.lifecycle).toLowerCase() } : {},
    isHumanApproved: row.is_human_approved === true,
    evidence: Array.isArray(row.evidence) ? row.evidence.flatMap((item) => {
      const parsed = parseEvidence(item);
      return parsed ? [parsed] : [];
    }) : [],
    ...seconds(row.last_verified_at) ? { lastVerifiedAt: seconds(row.last_verified_at) } : {},
    ...seconds(row.last_seen_at) ? { lastSeenAt: seconds(row.last_seen_at) } : {}
  };
}
var TOKEN_CATEGORY_LABEL = {
  native: "Native coin",
  stablecoin: "Stablecoin",
  wrapped_native: "Wrapped native coin",
  wrapped: "Wrapped asset",
  bridged: "Bridged variant",
  governance: "Governance token",
  lst: "Liquid staking token",
  meme: "Meme token",
  defi: "DeFi token",
  tokenized_security: "Tokenized security"
};
function tokenCategoryLabel(category) {
  const key = (category ?? "").trim().toLowerCase();
  if (!key || key === "unknown") return void 0;
  return TOKEN_CATEGORY_LABEL[key] ?? key.replace(/[_-]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}
var TOKEN_CATEGORY_UNKNOWN_REASON = "Not classified. No data provider supplies a token's role; it comes from the bundled token registry or from a person, and neither has set one for this token.";
function tokenConfidence(facts) {
  if (facts.confidenceScored === true && facts.confidence !== void 0) {
    return { scored: true, percent: Math.round(facts.confidence * 100) };
  }
  if (facts.confidenceScored === null) {
    return {
      scored: false,
      reason: "Not scored \u2014 this row was written before token confidence was computed."
    };
  }
  return { scored: false, reason: "Not scored \u2014 nothing has computed a confidence for this row." };
}
function tokenLogoRef(logoUri) {
  const raw = (logoUri ?? "").trim();
  if (!raw) return void 0;
  const s3 = /^s3:\/\/[^/]+\/(.+)$/i.exec(raw);
  if (s3?.[1]) return { kind: "key", key: s3[1] };
  if (/^https?:\/\//i.test(raw)) return { kind: "url", url: raw };
  if (raw.replace(/^\/+/, "").startsWith(`${CDN_ASSET_ROOT}/`)) {
    return { kind: "key", key: raw.replace(/^\/+/, "") };
  }
  return { kind: "unrenderable", stored: raw };
}
var TOKEN_KIND_LABEL = {
  native: "Native coin",
  erc20: "ERC-20",
  trc20: "TRC-20",
  erc721: "ERC-721",
  erc1155: "ERC-1155",
  spl: "SPL token",
  "spl-2022": "SPL Token-2022",
  nep141: "NEP-141",
  hip1: "HIP-1",
  metaprotocol: "Metaprotocol token",
  cosmos_bank: "Cosmos bank denom",
  // A non-fungible collection on no token standard (CryptoPunks predates ERC-721).
  nft: "NFT (no token standard)"
};
var EVM_DEFAULT_NOTE = "The stored definition says ERC-20, which is what the token service records for an EVM contract it has not classified \u2014 it is not a checked standard.";
function tokenStandard(input) {
  const stored = (input.storedKind ?? "").trim().toLowerCase();
  const declared = input.declared?.resolved ? input.declared : null;
  const disagrees = (kind) => stored && stored !== kind && stored !== "unknown" ? stored === "erc20" ? EVM_DEFAULT_NOTE : `The stored definition says ${TOKEN_KIND_LABEL[stored] ?? stored}.` : void 0;
  const withNote = (reading, kind) => {
    const note = disagrees(kind);
    return note ? { ...reading, storedDisagrees: note } : reading;
  };
  if (stored === "native") {
    return {
      kind: "native",
      label: TOKEN_KIND_LABEL.native,
      basis: "native",
      basisText: "The chain's own coin \u2014 it has no token contract."
    };
  }
  if (declared?.erc721 || declared?.erc1155) {
    const kind = declared.erc721 ? "erc721" : "erc1155";
    return withNote(
      {
        kind,
        label: `${TOKEN_KIND_LABEL[kind]} collection`,
        basis: "declared",
        basisText: "Declared by the contract itself (ERC-165)."
      },
      kind
    );
  }
  if (stored === "erc721" || stored === "erc1155") {
    return {
      kind: stored,
      label: `${TOKEN_KIND_LABEL[stored]} collection`,
      basis: "recorded",
      basisText: "As recorded on the stored definition."
    };
  }
  if (stored === "nft") {
    return {
      kind: "nft",
      label: "NFT collection",
      basis: "recorded",
      basisText: "Recorded as a non-fungible collection on no token standard \u2014 it predates or ignores ERC-721, so the contract declares none."
    };
  }
  const catalogNft = (input.catalogType ?? "").trim().toLowerCase() === "nft";
  if (catalogNft && input.decimals == null) {
    return withNote(
      {
        kind: "nft",
        label: "NFT collection",
        basis: "catalogued",
        basisText: "Catalogued as an NFT collection; the contract has not confirmed which standard."
      },
      "nft"
    );
  }
  if (stored && stored !== "unknown") {
    const label = TOKEN_KIND_LABEL[stored] ?? stored.toUpperCase();
    if (stored === "erc20") {
      return {
        kind: stored,
        label,
        basis: "recorded",
        basisText: input.decimals != null ? `As recorded \u2014 and it has a decimal scale (${input.decimals}), which only a fungible token defines.` : "As recorded, but with no decimal scale \u2014 the token service records ERC-20 for an EVM contract it has not classified, so the standard is unconfirmed."
      };
    }
    return { kind: stored, label, basis: "recorded", basisText: "As recorded on the stored definition." };
  }
  return {
    kind: "unknown",
    label: "Not recorded",
    basis: "unknown",
    basisText: "The stored definition does not record a token standard, and the contract was not asked."
  };
}

// src/tokens/metadata.ts
var tokenImpersonationSchema = z.object({
  claimed: z.string(),
  field: z.enum(["symbol", "name"]),
  symbol: z.string().min(1),
  name: z.string().nullish(),
  address: z.string().nullish(),
  native: z.boolean().default(false)
});
function impersonationSentence(claim) {
  const real = claim.name ? `${claim.name} (${claim.symbol})` : claim.symbol;
  const where = claim.native ? "the chain's own coin, which has no token contract" : claim.address ? `the real one is ${shortenAddress(claim.address)}` : "at another address";
  return `Calls itself ${claim.claimed}, but it is not ${real} \u2014 ${where}.`;
}
function counterfeitTokenFlag(claims) {
  const [first] = claims;
  if (!first) return null;
  if (claims.length === 1) {
    const sentence = impersonationSentence(first);
    return transactionFlag(
      "counterfeit_token",
      `This transaction moves a token that ${sentence.charAt(0).toLowerCase()}${sentence.slice(1)}`
    );
  }
  const posedAs = [...new Set(claims.map((c) => c.symbol))];
  const list = posedAs.length === 1 ? posedAs[0] : `${posedAs.slice(0, -1).join(", ")} and ${posedAs[posedAs.length - 1]}`;
  return transactionFlag(
    "counterfeit_token",
    `This transaction moves ${claims.length} tokens posing as ${list} \u2014 none of them is the real asset.`
  );
}
function titledSymbol(symbol, impersonates) {
  if (!symbol) return void 0;
  return impersonates ? `counterfeit ${symbol}` : symbol;
}
function parseTokenImpersonation(raw) {
  const parsed = tokenImpersonationSchema.safeParse(raw);
  if (!parsed.success) return void 0;
  const { claimed, field, symbol, name, address, native } = parsed.data;
  return {
    claimed,
    field,
    symbol,
    ...name ? { name } : {},
    ...address ? { address } : {},
    native
  };
}
var TOKEN_BATCH_KEYS = 100;
var tokenRowSchema = z.object({
  chain_id: z.string().min(1),
  identifier: z.string().min(1),
  symbol: z.string().nullish(),
  name: z.string().nullish(),
  decimals: z.number().int().min(0).max(36).nullish(),
  kind: z.string().nullish(),
  is_spam: z.boolean().nullish(),
  impersonates: z.unknown().optional()
}).passthrough();
var tokenBatchSchema = z.object({
  items: z.array(z.unknown()).default([]),
  missing: z.array(z.unknown()).default([])
});
function tokenMetadataKey(chainId, address) {
  const chain = toCanonicalChainId(chainId) ?? (chainId ?? "").trim().toLowerCase();
  return `${chain}|${foldTokenIdentifier(address, chain || void 0)}`;
}
var NATIVE_TOKEN_PREFIX = "native:";
function nativeTokenChain(identifier) {
  const raw = (identifier ?? "").trim();
  if (raw.slice(0, NATIVE_TOKEN_PREFIX.length).toLowerCase() !== NATIVE_TOKEN_PREFIX) return null;
  const chain = raw.slice(NATIVE_TOKEN_PREFIX.length).trim();
  return chain ? toCanonicalChainId(chain) : null;
}
function nativeTokenIdentifier(chainId) {
  const chain = toCanonicalChainId(chainId);
  return chain ? `${NATIVE_TOKEN_PREFIX}${chain}` : null;
}
function foldTokenIdentifier(identifier, chainId) {
  const native = nativeTokenChain(identifier);
  if (native) return `${NATIVE_TOKEN_PREFIX}${native}`;
  return normalizeAddress(identifier, chainId ?? void 0);
}
var EMPTY_INDEX = {
  get: () => void 0,
  byKey: /* @__PURE__ */ new Map(),
  size: 0
};
function buildTokenMetadataIndex(rows) {
  if (rows.length === 0) return EMPTY_INDEX;
  const byKey = /* @__PURE__ */ new Map();
  for (const row of rows) byKey.set(tokenMetadataKey(row.chainId, row.address), row);
  return {
    byKey,
    size: byKey.size,
    get(address, chainId) {
      if (!address) return void 0;
      return byKey.get(tokenMetadataKey(chainId, address));
    }
  };
}
function emptyTokenMetadataIndex() {
  return EMPTY_INDEX;
}
function tokenPricesFromIndex(index) {
  const byAddress = /* @__PURE__ */ new Map();
  for (const row of index.byKey.values()) {
    const key = foldTokenIdentifier(row.address, row.chainId);
    const bucket = byAddress.get(key);
    if (bucket) bucket.push(row);
    else byAddress.set(key, [row]);
  }
  const prices = {};
  const ambiguous = [];
  for (const [key, rows] of byAddress) {
    const [first, ...rest] = rows;
    if (!first) continue;
    const agrees = rest.every(
      (row) => row.decimals === first.decimals && row.symbol === first.symbol
    );
    if (!agrees) {
      ambiguous.push(key);
      continue;
    }
    prices[key] = {
      token_address: first.address,
      chain_id: first.chainId,
      symbol: first.symbol ?? null,
      decimals: first.decimals ?? null,
      // Not a price read. See the module docstring: absent, never guessed.
      price_usd: null,
      block_number: null,
      block_timestamp: null,
      source: "token-definition",
      confidence: null
    };
  }
  return { prices, ambiguous };
}
function graphTokenRefs(graph) {
  const edges = graph?.edges;
  if (!Array.isArray(edges)) return [];
  const weights = /* @__PURE__ */ new Map();
  for (const raw of edges) {
    if (!raw || typeof raw !== "object") continue;
    const edge = raw;
    const kind = typeof edge.kind === "string" ? edge.kind : "";
    if (NON_FUNGIBLE_FLOW_KINDS.has(kind)) continue;
    const address = typeof edge.token_address === "string" ? edge.token_address.trim() : "";
    if (!address) continue;
    const chainId = [edge.token_chain_id, edge.from_chain, edge.to_chain].find(
      (value) => typeof value === "string" && value.trim() !== ""
    );
    if (!chainId) continue;
    const key = tokenMetadataKey(chainId, address);
    const existing = weights.get(key);
    if (existing) existing.edges += 1;
    else weights.set(key, { ref: { chainId, address }, edges: 1 });
  }
  return [...weights.values()].sort((a, b) => b.edges - a.edges).map((entry) => entry.ref);
}
function boundTokenRefs(refs, limit) {
  const seen = /* @__PURE__ */ new Map();
  for (const ref of refs) {
    if (!ref.address || !ref.chainId) continue;
    const key = tokenMetadataKey(ref.chainId, ref.address);
    if (!seen.has(key)) seen.set(key, ref);
  }
  const unique = [...seen.values()];
  const cap = Number.isFinite(limit) ? Math.max(0, Math.floor(limit)) : 0;
  return {
    refs: unique.slice(0, cap),
    requestedCount: unique.length,
    truncated: unique.length > cap
  };
}
function chunkTokenRefs(refs, size = TOKEN_BATCH_KEYS) {
  const step = Math.max(1, Math.min(Math.floor(size), TOKEN_BATCH_KEYS));
  const chunks = [];
  for (let i = 0; i < refs.length; i += step) chunks.push(refs.slice(i, i + step));
  return chunks;
}
function parseTokenBatch(payload) {
  const parsed = tokenBatchSchema.safeParse(payload);
  if (!parsed.success) return [];
  const rows = [];
  for (const item of parsed.data.items) {
    const row = tokenRowSchema.safeParse(item);
    if (!row.success) continue;
    const { chain_id, identifier, name, symbol, decimals, kind, is_spam, impersonates } = row.data;
    const claim = parseTokenImpersonation(impersonates);
    rows.push({
      chainId: chain_id,
      address: identifier,
      ...name && name.trim() ? { name: name.trim() } : {},
      // Chain-written text is attacker-controlled: an empty or whitespace-only
      // symbol is not a ticker, and rendering it leaves a gap where a reader
      // expects a name.
      ...symbol && symbol.trim() ? { symbol: symbol.trim() } : {},
      ...typeof decimals === "number" ? { decimals } : {},
      ...kind ? { kind } : {},
      isSpam: is_spam === true,
      ...claim ? { impersonates: claim } : {},
      facts: parseTokenFacts(item)
    });
  }
  return rows;
}
function identifierOf(ref) {
  const chain = toCanonicalChainId(ref.chainId) ?? ref.chainId;
  return foldTokenIdentifier(ref.address, chain);
}
async function fetchChunk(client, refs, options) {
  const keys = refs.map((ref) => ({
    chain_id: toCanonicalChainId(ref.chainId) ?? ref.chainId,
    identifier: identifierOf(ref)
  }));
  const response = await client.post(
    "/tokens/batch",
    { keys },
    {
      signal: options.signal,
      cacheTtlMs: options.cacheTtlMs ?? 0
    }
  );
  return parseTokenBatch(unwrapData(response));
}
async function fetchTokenMetadata(client, refs, options = {}) {
  const chunks = chunkTokenRefs(refs);
  if (chunks.length === 0) return [];
  const settled = await Promise.allSettled(
    chunks.map((chunk) => fetchChunk(client, chunk, options))
  );
  const rows = [];
  let answered = 0;
  for (const result of settled) {
    if (result.status !== "fulfilled") continue;
    answered += 1;
    rows.push(...result.value);
  }
  if (answered === 0) {
    const [first] = settled;
    throw first && first.status === "rejected" ? first.reason : new Error("token metadata batch failed");
  }
  return rows;
}

// src/storage/tx-body.ts
function abiSourceOf(value, ...fallbacks) {
  const fine = s(rec2(value?.metadata)?.abi_source);
  if (fine) return fine;
  for (const candidate of fallbacks) {
    const coarse = s(candidate);
    if (coarse) return coarse;
  }
  return void 0;
}
function decodeConfidence(value) {
  const evidence = value?.evidence;
  const metadata = rec2(value?.metadata);
  const explanation = value?.explanation ?? metadata?.abi_explanation;
  const records = Array.isArray(evidence) ? evidence : rec2(evidence) ? [evidence] : [];
  const abiScope = s(metadata?.abi_scope) ?? s(rec2(rec2(records[0])?.context)?.scope);
  return {
    confidence: recordedConfidence(value),
    evidence: records,
    explanation: Array.isArray(explanation) ? explanation.filter((v) => typeof v === "string") : [],
    ...abiScope ? { abiScope } : {}
  };
}
var ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;
var TRANSFER_KINDS = {
  native_transfer: "native",
  erc20_transfer: "erc20",
  nft_transfer: "nft"
};
function looksLikeAddress(value) {
  return typeof value === "string" && ADDRESS_RE.test(value);
}
var ADDRESS_FACT_KINDS = /* @__PURE__ */ new Set(["address", "token"]);
function isAddressFactKind(kind) {
  return Boolean(kind && ADDRESS_FACT_KINDS.has(kind));
}
function rec2(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : void 0;
}
function s(value) {
  return value == null || value === "" ? void 0 : String(value);
}
function addrStr(value) {
  if (typeof value === "string") return value || void 0;
  const o = rec2(value);
  if (!o) return void 0;
  return s(o.value) ?? s(o.address) ?? s(o.hash);
}
var DECODED_ENVELOPE_MARKER = "@decoded";
function isEnriched(value) {
  const o = rec2(value);
  return !!o && !!rec2(o[DECODED_ENVELOPE_MARKER]);
}
function displayOf(value) {
  const inner = isEnriched(value) ? rec2(value[DECODED_ENVELOPE_MARKER]) : void 0;
  return inner ? inner.display : value;
}
function rawOf(value) {
  const inner = isEnriched(value) ? rec2(value[DECODED_ENVELOPE_MARKER]) : void 0;
  return inner ? inner.raw : value;
}
function decodedOf(value) {
  const inner = isEnriched(value) ? rec2(value[DECODED_ENVELOPE_MARKER]) : void 0;
  return inner ? inner.decoded : void 0;
}
function kindOf(value) {
  const inner = isEnriched(value) ? rec2(value[DECODED_ENVELOPE_MARKER]) : void 0;
  return inner ? s(inner.kind) : void 0;
}
function params(value) {
  if (!Array.isArray(value)) return [];
  return value.map((p) => {
    const o = rec2(p) ?? {};
    const display = "display" in o ? o.display : isEnriched(o.value) ? displayOf(o.value) : void 0;
    const semanticType = s(o.semantic_type) ?? kindOf(o.value);
    return {
      name: s(o.name) ?? "",
      type: s(o.type) ?? "",
      value: o.value,
      display: display ?? void 0,
      semanticType,
      canonicalName: s(o.canonical_name) ?? void 0
    };
  });
}
function proxyChain(value) {
  return Array.isArray(value) ? value.map((v) => String(v)).filter(Boolean) : [];
}
function parseDecodedCallValue(value) {
  return toCall(value);
}
function toCall(value) {
  const o = rec2(value);
  if (!o) return null;
  const label = s(o.label) ?? s(o.name) ?? s(o.function);
  const signature = s(o.signature);
  const address = addrStr(o.address) ?? addrStr(o.abi_address);
  const ps = params(o.params);
  const returnParams = Array.isArray(o.decoded_return) ? params(o.decoded_return) : params(
    rec2(o.decoded_return)?.params ?? rec2(rec2(o.metadata)?.decoded_return)?.params ?? o.return_params ?? o.returns
  );
  if (!label && !signature && ps.length === 0 && returnParams.length === 0 && !address) return null;
  const revert = callRevert(o);
  return {
    label,
    signature,
    address,
    source: abiSourceOf(o, o.source, o.abi_source),
    ...decodeConfidence(o),
    params: ps,
    returnParams,
    ...revert ? { revert } : {},
    proxyChain: proxyChain(o.proxy_chain),
    claims: Array.isArray(rec2(o.metadata)?.claims) ? rec2(o.metadata).claims.flatMap((value2) => {
      const c = rec2(value2);
      return c && s(c.claim) && s(c.status) ? [{ claim: String(c.claim), status: String(c.status), reason: s(c.reason) ?? "", evidence: c.evidence }] : [];
    }) : void 0,
    economicReconciliation: (() => {
      const e = rec2(rec2(o.metadata)?.economic_reconciliation);
      return e && s(e.status) ? { status: String(e.status), rows: Array.isArray(e.rows) ? e.rows.flatMap((r) => rec2(r) ? [rec2(r)] : []) : [], gaps: Array.isArray(e.gaps) ? e.gaps.filter((g) => typeof g === "string") : [] } : void 0;
    })()
  };
}
function callRevert(o) {
  const meta = rec2(rec2(o.metadata)?.revert);
  const revertParams = params(o.decoded_revert);
  if (!meta && revertParams.length === 0) return void 0;
  return {
    error: s(meta?.error),
    signature: s(meta?.signature),
    selector: s(meta?.selector),
    raisedBy: addrStr(meta?.raised_by),
    source: s(meta?.source),
    params: revertParams
  };
}
function decodeCarrier(body) {
  const root = rec2(body) ?? {};
  return rec2(root.canonical_transaction) ?? root;
}
function extras(body) {
  return rec2(decodeCarrier(body).extras) ?? {};
}
function enrichmentLayers(body) {
  const root = rec2(body) ?? {};
  return asArray(rec2(root.extras)?.enrichment_layers) ?? asArray(root.enrichment_layers) ?? asArray(extras(body).enrichment_layers) ?? asArray(decodeCarrier(body).enrichment_layers) ?? [];
}
function triggerEnvelopeFromBody(body) {
  const root = rec2(body);
  const canonical = rec2(root?.canonical_transaction) ?? rec2(root?.canonical);
  if (!canonical || typeof canonical.chain_id !== "string" || typeof canonical.tx_hash !== "string") return null;
  const layers = enrichmentLayers(body).filter((layer) => Array.isArray(rec2(layer)?.enrichments));
  return { canonical, enrichment_layers: layers };
}
function enrichmentPayload(body, kind) {
  const layers = enrichmentLayers(body);
  for (const layer of layers ?? []) {
    for (const e of asArray(rec2(layer)?.enrichments) ?? []) {
      const eo = rec2(e);
      if (eo && s(eo.kind) === kind) return rec2(eo.payload);
    }
  }
  return void 0;
}
function bodyNativePrice(body) {
  for (const layer of enrichmentLayers(body)) {
    for (const e of asArray(rec2(layer)?.enrichments) ?? []) {
      const eo = rec2(e);
      if (!eo || s(eo.kind) !== "price") continue;
      const payload = rec2(eo.payload);
      const token = s(payload?.token_address);
      if (token && token.toLowerCase() !== NATIVE_TOKEN) continue;
      const priceUsd = Number(payload?.price_usd);
      if (!Number.isFinite(priceUsd) || priceUsd <= 0) continue;
      const quotedAt = Number(payload?.quote_as_of ?? payload?.as_of);
      return {
        priceUsd,
        ...Number.isFinite(quotedAt) && quotedAt > 0 ? { quotedAt } : {},
        ...s(eo.source) ? { source: s(eo.source) } : {}
      };
    }
  }
  return void 0;
}
function bodyTokenDefinitions(body) {
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const layer of enrichmentLayers(body)) {
    for (const e of asArray(rec2(layer)?.enrichments) ?? []) {
      const eo = rec2(e);
      if (!eo || s(eo.kind) !== "token_definition") continue;
      const payload = rec2(eo.payload);
      const chainId = s(payload?.chain_id);
      const raw = s(payload?.token_address);
      if (!chainId || !raw) continue;
      const address = normalizeAddress(raw, chainId);
      const key = `${chainId}|${address}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const impersonates = parseTokenImpersonation(payload?.impersonates);
      out.push({ chainId, address, isSpam: payload?.is_spam === true, ...impersonates ? { impersonates } : {} });
    }
  }
  return out;
}
var GATE_REASON_RE = /is below the (configured|default) \S+ floor of ([0-9.]+)/;
function gateOutcome(value) {
  const o = rec2(value);
  const domain = s(o?.domain);
  const action = s(o?.action);
  const reason = s(o?.reason);
  const confidence = numOrUndef(o?.confidence);
  const floor = numOrUndef(o?.floor);
  if (!o || !domain || !action || !reason || confidence === void 0 || floor === void 0) {
    return null;
  }
  return {
    domain,
    subject: s(o.subject) ?? null,
    confidence,
    floor,
    action,
    requested_action: s(o.requested_action),
    floor_is_configured: typeof o.floor_is_configured === "boolean" ? o.floor_is_configured : void 0,
    reason,
    ...s(o.floor_source) ? { floor_source: s(o.floor_source) } : {},
    ...s(o.floor_path) ? { floor_path: s(o.floor_path) } : {},
    ...s(o.action_source) ? { action_source: s(o.action_source) } : {},
    ...rec2(o.score_components) ? { score_components: rec2(o.score_components) } : {},
    ...Array.isArray(o.score_explanation) ? { score_explanation: o.score_explanation.filter((line) => typeof line === "string") } : {}
  };
}
function parseConfidenceGate(body) {
  const root = rec2(body) ?? {};
  const raw = rec2(rec2(root.extras)?.confidence_gate) ?? rec2(extras(body).confidence_gate);
  if (raw) {
    const execution = parsePipelineExecution(body);
    const scope = s(raw.execution_scope) ?? s(execution?.execution_scope);
    const pipelineId = s(raw.pipeline_id) ?? s(execution?.pipeline_id);
    const pipelineRevision = s(raw.pipeline_revision) ?? s(execution?.pipeline_revision);
    const snapshot = rec2(rec2(rec2(execution?.applied_configuration)?.confidence_policy)?.resolved);
    const matchingSnapshot = snapshot && (!s(raw.policy_hash) || raw.policy_hash === snapshot.hash) ? snapshot : null;
    const recordedFloor = rec2(rec2(matchingSnapshot?.effective)?.floors);
    const sources = rec2(matchingSnapshot?.sources);
    const outcomes = (asArray(raw.outcomes) ?? []).map(gateOutcome).filter((o) => o !== null).map((o) => {
      const path = `floors.${o.domain}`;
      const floor = numOrUndef(recordedFloor?.[o.domain]);
      const source = s(sources?.[path]);
      return !o.floor_source && o.floor_is_configured !== false && source && floor !== void 0 && Math.round(floor * 1e4) / 1e4 === o.floor ? { ...o, floor_source: source, floor_path: path } : o;
    });
    const evaluated = {};
    for (const [domain, count] of Object.entries(rec2(raw.evaluated) ?? {})) {
      const n = numOrUndef(count);
      if (n !== void 0) evaluated[domain] = n;
    }
    return {
      evaluated,
      outcomes,
      omitted: numOrUndef(raw.omitted) ?? 0,
      truncated: numOrUndef(raw.truncated) ?? 0,
      ...s(raw.policy_hash) ? { policyHash: s(raw.policy_hash) } : {},
      ...scope || pipelineId || pipelineRevision ? { processing: {
        ...scope ? { scope } : {},
        ...pipelineId ? { pipelineId } : {},
        ...pipelineRevision ? { pipelineRevision } : {}
      } } : {},
      source: "record"
    };
  }
  const action = enrichmentPayload(body, "tx_action");
  const evidence = rec2(action?.evidence);
  if (!action || evidence?.low_confidence !== true) return null;
  const explanation = (asArray(action.explanation) ?? []).map(s).filter((line) => Boolean(line));
  const reason = explanation.find((line) => GATE_REASON_RE.test(line));
  const match = reason?.match(GATE_REASON_RE);
  const confidence = numOrUndef(action.confidence);
  if (!reason || !match || confidence === void 0) return null;
  return {
    evaluated: { tx_semantic: 1 },
    outcomes: [
      {
        domain: "tx_semantic",
        subject: s(action.action) ?? null,
        confidence,
        floor: Number(match[2]),
        action: evidence.withheld === true ? "withhold" : "flag",
        floor_is_configured: match[1] === "configured",
        reason
      }
    ],
    omitted: 0,
    truncated: 0,
    source: "tx_action"
  };
}
function parseTxRisk(body) {
  const out = [];
  for (const layer of enrichmentLayers(body)) {
    for (const e of asArray(rec2(layer)?.enrichments) ?? []) {
      const eo = rec2(e);
      if (!eo || s(eo.kind) !== "risk_score") continue;
      const p = rec2(eo.payload);
      if (!p || (s(p.scope) ?? "transaction") !== "transaction") continue;
      const score = Number(p.score);
      if (!Number.isFinite(score) || score <= 0) continue;
      const level = coerceRisk(p.bucket) ?? riskLevelFromScore(score);
      if (!level || level === RiskLevel.SAFE) continue;
      const signals = [];
      for (const raw of asArray(p.signals) ?? []) {
        const sig = rec2(raw);
        const signalId = s(sig?.signal_id);
        if (!sig || !signalId) continue;
        const details = rec2(sig.details);
        signals.push({
          signalId,
          contribution: Number(sig.contribution) || 0,
          severity: s(sig.severity),
          matchedKind: s(sig.matched_enrichment_kind),
          // A counterfeit token's signal names the asset it claims to be:
          // "Counterfeit token claim: USDT".
          subject: s(sig.matched_catalog_id) ?? s(details?.name) ?? s(rec2(details?.impersonates)?.claimed)
        });
      }
      out.push({ policyId: s(p.policy_id) ?? "risk", score, level, signals });
    }
  }
  return out.sort((a, b) => RISK_LEVEL_RANK[b.level] - RISK_LEVEL_RANK[a.level] || b.score - a.score);
}
function parseTxMeaning(body) {
  const action = enrichmentPayload(body, "tx_action");
  if (!action) return null;
  const evidence = rec2(action.evidence);
  const label = s(action.action);
  const confidence = numOrUndef(action.confidence);
  return {
    action: label ?? null,
    confidence: confidence === void 0 ? null : confidence,
    lowConfidence: evidence?.low_confidence === true
  };
}
function parsePipelineExecution(body) {
  const root = rec2(body);
  const record = rec2(rec2(root?.extras)?.pipeline_execution) ?? rec2(extras(body).pipeline_execution);
  return record?.version === 1 ? record : null;
}
function integrationOutputsFromBody(body) {
  const out = [];
  for (const layer of enrichmentLayers(body)) {
    for (const e of asArray(rec2(layer)?.enrichments) ?? []) {
      const eo = rec2(e);
      if (!eo) continue;
      const source = s(eo.source) ?? "";
      const fromCustomStep = s(eo.step_name) === "custom_step" || source.startsWith("custom:");
      if (!fromCustomStep) continue;
      const payload = rec2(eo.payload) ?? {};
      const runtimeId = (source.startsWith("custom:") ? source.slice("custom:".length) : void 0) ?? s(payload.runtime_id) ?? "unknown";
      out.push({
        runtimeId,
        kind: s(eo.kind) ?? "custom",
        payload,
        nodeName: s(eo.node_name) ?? s(eo.step_node) ?? void 0
      });
    }
  }
  return out;
}
function integrationRan(run) {
  return run.status === "ok";
}
function integrationRunsFromBody(body) {
  const rows = asArray(parsePipelineExecution(body)?.integrations) ?? [];
  const out = [];
  for (const row of rows) {
    const o = rec2(row);
    const runtimeId = s(o?.runtime_id);
    if (!o || !runtimeId) continue;
    out.push({
      runtimeId,
      status: s(o.status) ?? "",
      reason: s(o.reason) ?? "",
      outputs: numOrUndef(o.outputs) ?? 0,
      summary: s(o.summary) ?? "",
      withheld: o.withheld === true,
      ...s(o.kind) ? { kind: s(o.kind) } : {}
    });
  }
  return out;
}
function decodedTx(body) {
  return enrichmentPayload(body, "decoded_transaction");
}
function decodedEventsByIndex(body) {
  const out = /* @__PURE__ */ new Map();
  for (const ev of asArray(decodedTx(body)?.decoded_events) ?? []) {
    const o = rec2(ev);
    const idx = numOrUndef(o?.log_index);
    if (o && idx !== void 0 && !out.has(idx)) out.set(idx, o);
  }
  return out;
}
function decodedInternalsByIndex(body) {
  const out = /* @__PURE__ */ new Map();
  for (const itx of asArray(decodedTx(body)?.decoded_internal_txs) ?? []) {
    const o = rec2(itx);
    const trace = asArray(o?.trace_address);
    const key = o?.internal_tx_index == null ? trace?.length === 1 ? numOrUndef(trace[0]) : void 0 : numOrUndef(o.internal_tx_index);
    if (o && key !== void 0 && Number.isInteger(key) && key >= 0 && !out.has(key)) out.set(key, o);
  }
  return out;
}
function allCanonicalEvents(body) {
  const list = asArray(decodeCarrier(body).events) ?? [];
  return list.map((e) => rec2(e)).filter((e) => !!e);
}
function eventsOfKind(body, kind) {
  return allCanonicalEvents(body).filter((e) => s(e.kind) === kind);
}
function rawProviderPayload(body) {
  const root = rec2(body) ?? {};
  const rpp = rec2(root.raw_provider_payload);
  return rec2(rpp?.payload) ?? rec2(root.raw_provider_payload) ?? {};
}
function numOrUndef(value) {
  if (value == null || value === "" || typeof value === "boolean") return void 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : void 0;
}
function byIndex(a, b) {
  return (a.index ?? 0) - (b.index ?? 0);
}
function asArray(value) {
  return Array.isArray(value) ? value : void 0;
}
var STATUS_SUCCESS = /* @__PURE__ */ new Set(["success", "succeeded", "ok", "confirmed", "1", "true"]);
var STATUS_FAILED = /* @__PURE__ */ new Set(["failed", "failure", "reverted", "revert", "error", "0", "false"]);
function coerceTxStatus(parts) {
  const raw = parts.status != null && parts.status !== "" ? String(parts.status) : void 0;
  const receiptStatus = numOrUndef(parts.receiptStatus);
  const error = parts.error != null && parts.error !== "" ? String(parts.error) : void 0;
  let kind = "unknown";
  if (receiptStatus === 1) kind = "success";
  else if (receiptStatus === 0) kind = "failed";
  else {
    const r = raw?.trim().toLowerCase();
    if (r && STATUS_SUCCESS.has(r)) kind = "success";
    else if (r && STATUS_FAILED.has(r)) kind = "failed";
    else if (r === "pending") kind = "pending";
    else if (error) kind = "failed";
  }
  return { kind, raw, receiptStatus, error };
}
function parseTxStatus(body) {
  const carrier = decodeCarrier(body);
  const dt = decodedTx(body) ?? {};
  const status = coerceTxStatus({
    status: s(carrier.status) ?? s(dt.status),
    receiptStatus: numOrUndef(carrier.receipt_status) ?? numOrUndef(dt.receipt_status),
    error: s(carrier.error) ?? s(dt.error)
  });
  if (status.kind === "unknown" && s(carrier.family) === "utxo") {
    const landed = carrier.block_height != null || s(carrier.block_hash) !== void 0;
    return { ...status, kind: landed ? "success" : "pending" };
  }
  return status;
}
function parseUtxoInputsOutputs(body) {
  const carrier = decodeCarrier(body);
  if (s(carrier.family) !== "utxo") return null;
  const inputs = (asArray(carrier.inputs) ?? []).flatMap((raw, position) => {
    const o = rec2(raw);
    if (!o) return [];
    return [
      {
        index: numOrUndef(o.index) ?? position,
        address: s(o.address),
        value: o.value_sat == null ? void 0 : String(o.value_sat),
        prevTxId: s(o.prev_tx_id),
        prevVout: numOrUndef(o.prev_vout),
        scriptType: s(o.script_type),
        coinbase: o.is_coinbase === true
      }
    ];
  });
  const outputs = (asArray(carrier.outputs) ?? []).flatMap((raw, position) => {
    const o = rec2(raw);
    if (!o) return [];
    return [
      {
        index: numOrUndef(o.index) ?? position,
        address: s(o.address),
        value: o.value_sat == null ? "0" : String(o.value_sat),
        scriptType: s(o.script_type),
        data: s(o.op_return_payload)
      }
    ];
  });
  const shieldedBody = rec2(carrier.shielded);
  const components = asArray(shieldedBody?.components) ?? [];
  const hidesAmounts = components.some((raw) => {
    const o = rec2(raw);
    return !!o && (s(o.kind) === "spend" || s(o.kind) === "action") && o.value_sat == null;
  });
  const poolBalances = {};
  for (const [pool, balance] of Object.entries(rec2(shieldedBody?.value_balance_sat) ?? {})) {
    if (balance != null && String(balance) !== "0") poolBalances[pool] = String(balance);
  }
  const shielded = components.flatMap((raw) => {
    const o = rec2(raw);
    if (!o) return [];
    const ring = asArray(o.ring_members);
    return [
      {
        pool: s(o.pool) ?? "shielded",
        kind: s(o.kind) ?? "leg",
        value: o.value_sat == null ? void 0 : String(o.value_sat),
        ringSize: ring && ring.length > 0 ? ring.length : void 0
      }
    ];
  });
  return {
    inputs,
    outputs,
    shielded,
    poolBalances,
    hidesAmounts,
    fee: carrier.fee_sat == null ? void 0 : String(carrier.fee_sat),
    vsize: numOrUndef(carrier.vsize),
    coinbase: carrier.is_coinbase === true
  };
}
function parseDecodedCall(body) {
  const carrier = decodeCarrier(body);
  const root = rec2(body) ?? {};
  const wrap = rec2(root.decoded_transaction);
  return (
    // Standard `decoded_transaction` enrichment — the single source of truth.
    toCall(decodedTx(body)?.decoded_call) ?? // Legacy: inlined on the body / Moralis row.
    toCall(extras(body).decoded_call) ?? toCall(carrier.decoded_call) ?? toCall(wrap?.decoded_call) ?? toCall(root.decoded_call)
  );
}
function topicsFrom(o) {
  if (Array.isArray(o.topics)) return o.topics.map((t) => String(t)).filter(Boolean);
  const out = [];
  for (const k of ["topic0", "topic1", "topic2", "topic3"]) {
    const v = s(o[k]);
    if (v) out.push(v);
  }
  return out;
}
function logFromCanonical(e, std) {
  const raw = rec2(e.raw_payload) ?? {};
  const decoded = std ?? rec2(e.decoded);
  return {
    index: numOrUndef(e.log_index ?? decoded?.log_index ?? raw.log_index),
    address: addrStr(e.address) ?? addrStr(decoded?.address) ?? addrStr(raw.address),
    name: s(decoded?.event) ?? s(decoded?.name),
    signature: s(decoded?.signature),
    source: abiSourceOf(decoded, decoded?.source, decoded?.abi_source),
    ...decodeConfidence(decoded),
    params: params(decoded?.params),
    topics: topicsFrom(raw),
    data: s(raw.data),
    raw: !decoded
  };
}
function logFromStandard(o) {
  return {
    index: numOrUndef(o.log_index),
    address: addrStr(o.address),
    name: s(o.event) ?? s(o.name),
    signature: s(o.signature),
    source: abiSourceOf(o, o.abi_source, o.source),
    ...decodeConfidence(o),
    params: params(o.params),
    topics: [],
    data: void 0,
    raw: false
  };
}
function logFromLegacy(o) {
  const decoded = rec2(o.decoded_event) ?? (typeof o.event === "string" ? o : void 0);
  return {
    index: numOrUndef(o.log_index),
    address: addrStr(o.address) ?? addrStr(decoded?.address),
    name: s(decoded?.event) ?? s(decoded?.name) ?? s(decoded?.label),
    signature: s(decoded?.signature),
    source: abiSourceOf(decoded, decoded?.source, decoded?.abi_source),
    ...decodeConfidence(decoded),
    params: params(decoded?.params),
    topics: topicsFrom(o),
    data: s(o.data),
    raw: !decoded
  };
}
function parseEvents(body) {
  const canonical = eventsOfKind(body, "log");
  if (canonical.length > 0) {
    const std2 = decodedEventsByIndex(body);
    return canonical.map((e) => {
      const idx = numOrUndef(e.log_index);
      return logFromCanonical(e, idx !== void 0 ? std2.get(idx) : void 0);
    }).sort(byIndex);
  }
  const std = asArray(decodedTx(body)?.decoded_events);
  if (std && std.length > 0) {
    return std.map((e) => logFromStandard(rec2(e) ?? {})).sort(byIndex);
  }
  const root = rec2(body) ?? {};
  const wrap = rec2(root.decoded_transaction);
  const legacy = asArray(wrap?.decoded_events) ?? asArray(root.logs) ?? asArray(rawProviderPayload(body).logs) ?? [];
  return legacy.map((l) => logFromLegacy(rec2(l) ?? {})).sort(byIndex);
}
var CREATION_CALL_TYPES = /* @__PURE__ */ new Set(["create", "create2"]);
function isCreationCallType(callType) {
  if (typeof callType !== "string") return false;
  return CREATION_CALL_TYPES.has(callType.trim().toLowerCase().replace(/[_\s-]/g, ""));
}
function traceAddress(value) {
  return (asArray(value) ?? []).map((v) => numOrUndef(v)).filter((n) => n !== void 0);
}
function creationFields(callType, source, to) {
  const isCreation = isCreationCallType(callType);
  const candidate = addrStr(source?.deployed_address) ?? (isCreation ? to : void 0);
  return {
    isCreation,
    deployedAddress: candidate && isValidAddress(candidate) ? candidate : void 0,
    deployedCodeHash: s(source?.deployed_code_hash),
    deployedContractType: s(source?.deployed_contract_type),
    deployedProtocolId: s(source?.deployed_protocol_id)
  };
}
function calldataUnavailable(source) {
  return rec2(source?.metadata)?.calldata === "unavailable" ? { calldataUnavailable: true } : {};
}
function executionFields(source, fallback) {
  const error = s(source?.error) ?? s(fallback?.error);
  const success = source?.success ?? fallback?.success;
  return { success: error ? false : typeof success === "boolean" ? success : void 0, error };
}
function internalFromCanonical(e, i, std) {
  const raw = rec2(e.raw_payload) ?? {};
  const decoded = std ?? rec2(e.decoded);
  const inputCall = toCall(decoded?.call) ?? toCall(decoded?.input) ?? toCall(decoded);
  const callType = s(raw.type ?? raw.call_type) ?? s(decoded?.call_type);
  const to = addrStr(e.address) ?? addrStr(raw.to ?? raw.to_address) ?? addrStr(decoded?.to_address) ?? addrStr(decoded?.address);
  return {
    index: numOrUndef(e.internal_tx_index ?? decoded?.internal_tx_index) ?? i,
    from: addrStr(raw.from ?? raw.from_address) ?? addrStr(decoded?.from_address),
    to,
    value: s(raw.value ?? raw.amount) ?? s(decoded?.value),
    call: inputCall,
    callType,
    traceAddress: traceAddress(decoded?.trace_address ?? raw.trace_address),
    ...calldataUnavailable(decoded),
    ...executionFields(decoded, raw),
    ...creationFields(callType, decoded, to)
  };
}
function internalFromLegacy(o, i) {
  const callType = s(o.type ?? o.call_type);
  const to = addrStr(o.to ?? o.to_address);
  return {
    index: numOrUndef(o.internal_tx_index) ?? i,
    from: addrStr(o.from ?? o.from_address),
    to,
    value: s(o.value ?? o.amount),
    // `.call` is the standard `DecodedInternalTx` shape; `.input`/`.decoded_call`
    // are the older wrapped / Moralis shapes.
    call: toCall(o.call) ?? toCall(o.input) ?? toCall(o.decoded_call),
    callType,
    traceAddress: traceAddress(o.trace_address),
    ...calldataUnavailable(o),
    ...executionFields(o),
    ...creationFields(callType, o, to)
  };
}
function parseInternalTxs(body) {
  const canonical = eventsOfKind(body, "internal_tx");
  if (canonical.length > 0) {
    const std = decodedInternalsByIndex(body);
    return canonical.map((e, i) => {
      const idx = numOrUndef(e.internal_tx_index);
      return internalFromCanonical(e, i, idx !== void 0 ? std.get(idx) : void 0);
    }).sort(byIndex);
  }
  const root = rec2(body) ?? {};
  const wrap = rec2(root.decoded_transaction);
  const legacy = (
    // `decoded_internal_txs` is the standard field; `decoded_internal_transactions`
    // is the older wrapped shape.
    asArray(decodedTx(body)?.decoded_internal_txs) ?? asArray(wrap?.decoded_internal_txs) ?? asArray(wrap?.decoded_internal_transactions) ?? asArray(root.internal_transactions) ?? asArray(rawProviderPayload(body).internal_transactions) ?? []
  );
  return legacy.map((t, i) => internalFromLegacy(rec2(t) ?? {}, i)).sort(byIndex);
}
var FLOW_TRANSFER_KINDS = {
  native: "native",
  internal: "native",
  closure: "native",
  erc20: "erc20",
  erc721: "nft",
  erc1155: "nft",
  spl: "spl",
  hip1: "hip1",
  nep141: "nep141",
  trc20: "trc20",
  metaprotocol: "metaprotocol",
  // A Cosmos x/bank denom (IBC voucher, token-factory denom): its own member so
  // nothing labels it "ERC-20"; the denom is the token id, kept verbatim.
  cosmos_bank: "cosmos_bank"
};
function bodyChainId(body) {
  const chainId = s(decodeCarrier(body).chain_id);
  return chainId && getChainMetadata(chainId) ? chainId : void 0;
}
function familyAddr(value, chainId) {
  const addr = addrStr(value);
  return addr && isValidAddress(addr, chainId) ? addr : void 0;
}
function flowToken(value, flowKind, chainId) {
  const address = familyAddr(value, chainId);
  if (address) return address;
  const id = typeof value === "string" ? value.trim() : void 0;
  return id && flowKind && TOKEN_FLOW_KINDS.has(flowKind) ? id : void 0;
}
function transferFromFlow(o, chainId) {
  const flowKind = s(o.kind);
  const kind = FLOW_TRANSFER_KINDS[flowKind ?? ""];
  if (!kind) return null;
  return {
    kind,
    from: familyAddr(o.from_address, chainId),
    to: familyAddr(o.to_address, chainId),
    value: s(o.raw_amount),
    token: flowToken(o.token_address, flowKind, chainId),
    tokenId: s(o.token_id),
    // Not `numOrUndef` alone: `Number(null)` is 0, and a leg whose decimals are
    // unknown read as zero-decimal would print base units as whole tokens.
    decimals: o.decimals == null ? void 0 : numOrUndef(o.decimals)
  };
}
function transferFrom(e, kind) {
  const raw = rec2(e.raw_payload) ?? e;
  return {
    kind,
    from: addrStr(raw.from ?? raw.from_address),
    to: addrStr(raw.to ?? raw.to_address),
    value: s(raw.value ?? raw.amount),
    token: addrStr(raw.address ?? raw.token_address ?? raw.contract_address),
    symbol: s(raw.token_symbol ?? raw.symbol),
    tokenId: s(raw.token_id ?? raw.tokenId)
  };
}
function parseTransfers(body) {
  const published = asArray(rec2(rec2(body)?.extras)?.token_flows);
  if (published) {
    const chainId = bodyChainId(body);
    return published.map((f) => {
      const o = rec2(f);
      return o ? transferFromFlow(o, chainId) : null;
    }).filter((t) => t !== null);
  }
  const out = [];
  for (const e of allCanonicalEvents(body)) {
    const kind = TRANSFER_KINDS[s(e.kind) ?? ""];
    if (kind) out.push(transferFrom(e, kind));
  }
  if (out.length > 0) return out;
  const payload = { ...rec2(body) ?? {}, ...rawProviderPayload(body) };
  const pushAll = (list, kind) => {
    for (const t of asArray(list) ?? []) {
      const o = rec2(t);
      if (o) out.push(transferFrom(o, kind));
    }
  };
  pushAll(payload.native_transfers, "native");
  pushAll(payload.erc20_transfers, "erc20");
  pushAll(payload.nft_transfers, "nft");
  return out;
}
function parseEntityHints(body) {
  const list = decodeCarrier(body).tx_entity_hints;
  if (!Array.isArray(list)) return [];
  const out = [];
  for (const h of list) {
    const o = rec2(h);
    const address = addrStr(o?.address);
    if (!o || !address) continue;
    out.push({
      address,
      entityName: s(o.entity_name),
      entityLogo: s(o.entity_logo),
      label: s(o.label),
      confidence: numOrUndef(o.confidence),
      source: s(o.source)
    });
  }
  return out;
}
function strList(value) {
  return asArray(value)?.map((v) => s(v)).filter((v) => !!v) ?? [];
}
function listOrUndef(value) {
  const list = strList(value);
  return list.length > 0 ? list : void 0;
}
function crossChainRef(value) {
  const o = rec2(value);
  const id = o && s(o.value);
  if (!o || !id) return null;
  return {
    linkageType: s(o.linkage_type),
    idLabel: s(o.id_label),
    value: id,
    role: s(o.role),
    // Omitted when the linkage named no far side — never an empty string a
    // reader would take for a chain.
    ...s(o.remote_chain_id) ? { remoteChainId: s(o.remote_chain_id) } : {},
    protocolId: s(o.protocol_id),
    sagaKind: s(o.saga_kind),
    source: s(o.source),
    relationshipType: s(o.relationship_type),
    linkageModel: s(o.linkage_model),
    confidence: numOrUndef(o.confidence),
    // Absent rather than empty when the protocol pins no completion set, so a
    // consumer can tell "declared nothing" from "declared an empty set" and fall
    // back to the canonical source/destination pair.
    completeRoles: listOrUndef(o.complete_roles)
  };
}
function renderHintsRaw(body) {
  const root = rec2(body) ?? {};
  return rec2(rec2(root.extras)?.render_hints) ?? rec2(root.render_hints) ?? rec2(extras(body).render_hints);
}
function valueDisplayHint(value) {
  const o = rec2(value);
  const kind = o && s(o.kind);
  const component = o && s(o.component);
  if (!o || !kind || !component) return null;
  return {
    protocolId: s(o.protocol_id),
    kind,
    component,
    label: s(o.label),
    icon: s(o.icon),
    fields: strList(o.fields),
    description: s(o.description)
  };
}
function eventViewHint(value) {
  const o = rec2(value);
  const event = o && s(o.event);
  const component = o && s(o.component);
  if (!o || !event || !component) return null;
  return {
    protocolId: s(o.protocol_id),
    event,
    signature: s(o.signature),
    component,
    label: s(o.label),
    summaryFields: strList(o.summary_fields)
  };
}
function callViewHint(value) {
  const o = rec2(value);
  const call = o && s(o.call);
  const component = o && s(o.component);
  if (!o || !call || !component) return null;
  return {
    protocolId: s(o.protocol_id),
    call,
    signature: s(o.signature),
    component,
    label: s(o.label),
    summaryFields: strList(o.summary_fields)
  };
}
function parseRenderHints(body) {
  const raw = renderHintsRaw(body);
  return {
    valueHints: (asArray(raw?.display_hints) ?? []).map(valueDisplayHint).filter((h) => h !== null),
    eventHints: (asArray(raw?.event_views) ?? []).map(eventViewHint).filter((h) => h !== null),
    callHints: (asArray(raw?.call_views) ?? []).map(callViewHint).filter((h) => h !== null)
  };
}
function keyAttribute(value) {
  const o = rec2(value);
  const key = o && s(o.key);
  const label = o && s(o.label);
  if (!o || !key || !label) return null;
  const raw = o.value;
  if (raw === null || raw === void 0 || raw === "") return null;
  return {
    protocolId: s(o.protocol_id),
    key,
    label,
    kind: s(o.kind),
    source: s(o.source),
    value: raw,
    ...decodeConfidence(o),
    confidence: numOrUndef(o.confidence),
    sourceRefs: (Array.isArray(o.source_refs) ? o.source_refs : []).flatMap((ref) => {
      const parsed = SceneSourceRefSchema.safeParse(ref);
      return parsed.success ? [parsed.data] : [];
    }),
    semanticNodeId: s(o.semantic_node_id),
    pathId: s(o.path_id),
    decimals: numOrUndef(o.decimals),
    symbol: s(o.symbol),
    // Strings only: a word is text, and anything else would render as
    // `[object Object]` where the number used to be.
    valueLabel: typeof o.value_label === "string" && o.value_label.trim() ? o.value_label : void 0,
    valueVocabulary: typeof o.value_vocabulary === "string" ? s(o.value_vocabulary) : void 0,
    valueLabelStated: o.value_label_stated === true ? true : void 0,
    icon: s(o.icon),
    description: s(o.description)
  };
}
function keyAttributesRaw(body) {
  const root = rec2(body) ?? {};
  return asArray(rec2(root.extras)?.key_attributes) ?? asArray(root.key_attributes) ?? asArray(extras(body).key_attributes) ?? [];
}
function keyAttributeGroup(value) {
  const o = rec2(value);
  if (!o) return null;
  const rawAttrs = asArray(o.attributes) ?? [o];
  const attributes = rawAttrs.map(keyAttribute).filter((a) => a !== null);
  if (attributes.length === 0) return null;
  return {
    protocolId: s(o.protocol_id) ?? attributes[0]?.protocolId,
    entityType: s(o.entity_type),
    index: numOrUndef(o.index),
    eventName: s(o.event_name),
    attributes
  };
}
function parseKeyAttributeGroups(value) {
  return (asArray(value) ?? []).map(keyAttributeGroup).filter((g) => g !== null);
}
function parseKeyAttributes(body) {
  return parseKeyAttributeGroups(keyAttributesRaw(body));
}
function parseCrossChainRefs(body) {
  const dt = decodedTx(body) ?? {};
  return (asArray(dt.cross_chain_refs) ?? []).map(crossChainRef).filter((r) => r !== null);
}
function parseDecodeMeta(body) {
  const dt = decodedTx(body) ?? {};
  const meta = rec2(dt.metadata) ?? {};
  return {
    primaryProtocolId: s(dt.primary_protocol_id),
    protocolIds: strList(dt.protocol_ids),
    crossChainRefs: parseCrossChainRefs(body),
    decoderName: s(dt.decoder_name),
    decoderVersion: s(dt.decoder_version),
    decodeStepVersion: s(meta.decode_step_version),
    catalogVersion: s(meta.catalog_version),
    decodedAt: s(meta.decoded_at),
    durationMs: numOrUndef(dt.duration_ms),
    strategiesUsed: strList(dt.strategies_used),
    enrichmentSources: strList(dt.enrichment_sources),
    renderHints: parseRenderHints(body),
    keyAttributes: parseKeyAttributes(body),
    ...unnamedProgramsOf(meta)
  };
}
function unnamedProgramsOf(meta) {
  const programs = strList(meta.unnamed_programs);
  return programs.length > 0 ? { unnamedPrograms: programs } : {};
}
var NON_ADDRESS_LEAF_KINDS = /* @__PURE__ */ new Set(["bytes", "string", "integer", "bool"]);
var NON_ADDRESS_TYPE_RE = /^(bytes(?!20$)\d*|string)$/;
function collectAddresses(value, out, chains, chainId) {
  if (looksLikeAddress(value)) {
    out.add(value);
    return;
  }
  if (isEnriched(value)) {
    const inner = rec2(value[DECODED_ENVELOPE_MARKER]) ?? {};
    const decoded = rec2(inner.decoded);
    const addressChain = typeof decoded?.chain_id === "string" ? decoded.chain_id : chainId;
    if (inner.kind === "address" && addressChain) {
      const address = typeof decoded?.address === "string" ? decoded.address : inner.display;
      if (typeof address === "string" && isValidAddress(address, addressChain)) {
        out.add(address);
        const key = normalizeAddress(address);
        const networks = chains.get(key) ?? /* @__PURE__ */ new Set();
        networks.add(addressChain);
        chains.set(key, networks);
      }
    }
    if (!(typeof inner.kind === "string" && NON_ADDRESS_LEAF_KINDS.has(inner.kind))) {
      collectAddresses(inner.display, out, chains, chainId);
    }
    collectAddresses(inner.decoded, out, chains, chainId);
    return;
  }
  if (Array.isArray(value)) {
    for (const v of value) collectAddresses(v, out, chains, chainId);
    return;
  }
  const o = rec2(value);
  if (o) for (const v of Object.values(o)) collectAddresses(v, out, chains, chainId);
}
var ROLE_PRIORITY = [
  "from",
  "to",
  "call-target",
  "event-emitter",
  "transfer",
  "internal",
  "param"
];
function buildAddressIndex(parsed) {
  const roles = /* @__PURE__ */ new Map();
  const display = /* @__PURE__ */ new Map();
  const chains = /* @__PURE__ */ new Map();
  const index = (addr, role) => {
    const key = normalizeAddress(addr);
    if (!display.has(key)) display.set(key, addr);
    const entry = roles.get(key) ?? /* @__PURE__ */ new Set();
    entry.add(role);
    roles.set(key, entry);
  };
  const addValidated = (addr, role) => {
    if (addr) index(addr, role);
  };
  const addTyped = (addr, role) => {
    if (addr && isValidAddress(addr, parsed.chainId)) index(addr, role);
  };
  const addParamAddrs = (ps) => {
    const found = /* @__PURE__ */ new Set();
    for (const p of ps) {
      if (typeof p.value === "string" && NON_ADDRESS_TYPE_RE.test((p.type ?? "").trim())) continue;
      if (p.type === "address" && typeof p.value === "string" && !looksLikeAddress(p.value)) {
        addTyped(p.value, "param");
        continue;
      }
      collectAddresses(p.value, found, chains, parsed.chainId);
    }
    for (const a of found) {
      if (chains.has(normalizeAddress(a))) addValidated(a, "param");
      else addTyped(a, "param");
    }
  };
  addValidated(parsed.from, "from");
  addValidated(parsed.to, "to");
  for (const input of parsed.utxo?.inputs ?? []) addValidated(input.address, "from");
  for (const output of parsed.utxo?.outputs ?? []) addValidated(output.address, "to");
  if (parsed.decodedCall) {
    addTyped(parsed.decodedCall.address, "call-target");
    addParamAddrs(parsed.decodedCall.params);
    addParamAddrs(parsed.decodedCall.returnParams);
  }
  for (const command of parsed.sui?.commands ?? []) {
    addValidated(command.package, "call-target");
    if (command.call) {
      addValidated(command.call.address, "call-target");
      addParamAddrs(command.call.params);
      addParamAddrs(command.call.returnParams);
    }
  }
  for (const ev of parsed.events) {
    addTyped(ev.address, "event-emitter");
    addParamAddrs(ev.params);
  }
  for (const t of parsed.transfers) {
    addTyped(t.from, "transfer");
    addTyped(t.to, "transfer");
    addTyped(t.token, "transfer");
  }
  for (const itx of parsed.internalTxs) {
    addTyped(itx.from, "internal");
    addTyped(itx.to, "internal");
    addValidated(itx.deployedAddress, "internal");
    if (itx.call) {
      addTyped(itx.call.address, "call-target");
      addParamAddrs(itx.call.params);
      addParamAddrs(itx.call.returnParams);
    }
  }
  const rank = (rs) => {
    for (let i = 0; i < ROLE_PRIORITY.length; i += 1) {
      const role = ROLE_PRIORITY[i];
      if (role && rs.has(role)) return i;
    }
    return ROLE_PRIORITY.length;
  };
  return [...roles.entries()].map(([key, rs]) => ({
    address: display.get(key) ?? key,
    roles: [...rs],
    ...chains.has(key) ? { chainIds: [...chains.get(key)] } : {}
  })).sort((a, b) => rank(new Set(a.roles)) - rank(new Set(b.roles)));
}
function parseTxBody(body, fromHint, toHint) {
  const carrier = decodeCarrier(body);
  const from = fromHint ?? addrStr(carrier.from_address);
  const to = toHint ?? addrStr(carrier.to_address);
  const decodedCall = parseDecodedCall(body);
  const events = parseEvents(body);
  const internalTxs = parseInternalTxs(body);
  const transfers = parseTransfers(body);
  const entityHints = parseEntityHints(body);
  const utxo = parseUtxoInputsOutputs(body);
  const tonMeta = rec2(decodedTx(body)?.metadata);
  const tonRows = asArray(tonMeta?.ton_messages);
  const ton = tonRows ? {
    finality: s(carrier.finality),
    logicalTime: s(tonMeta?.logical_time),
    traceId: s(tonMeta?.trace_id),
    traceAvailable: tonMeta?.trace_available === true,
    messages: tonRows.flatMap((value) => {
      const row = rec2(value);
      if (!row || typeof row.id !== "string") return [];
      const decoded = rec2(row.decoded);
      const delivery = rec2(row.delivery);
      return [{
        id: row.id,
        direction: s(row.direction) ?? "unknown",
        kind: s(row.kind) ?? "unknown",
        source: s(row.source),
        destination: s(row.destination),
        value: s(row.value),
        createdLt: s(row.created_lt),
        operation: s(decoded?.function),
        gap: s(rec2(decoded?.metadata)?.decode_gap),
        bounced: row.bounced === true,
        deliveryHash: s(delivery?.transaction_hash),
        decodedParams: rec2(decoded?.params) ?? void 0,
        deliveryFailed: delivery?.aborted === true || rec2(delivery?.compute)?.success === false || rec2(delivery?.action)?.success === false
      }];
    })
  } : null;
  const sui = tonMeta?.family === "sui" ? {
    finality: s(tonMeta.finality),
    checkpoint: s(tonMeta.checkpoint),
    epoch: s(tonMeta.epoch),
    gasSponsor: s(tonMeta.gas_sponsor),
    executionError: tonMeta.execution_error ? JSON.stringify(tonMeta.execution_error) : void 0,
    commands: (asArray(tonMeta.sui_commands) ?? []).flatMap((value) => {
      const command = rec2(value);
      return command && typeof command.index === "number" ? [{
        index: command.index,
        kind: s(command.kind) ?? "unknown",
        package: s(command.package),
        call: toCall(command.decoded)
      }] : [];
    }),
    objectChanges: asArray(tonMeta.object_changes) ?? [],
    balanceChanges: asArray(tonMeta.balance_changes) ?? [],
    eventsComplete: tonMeta.events_complete === true,
    gaps: (asArray(tonMeta.decode_gaps) ?? []).flatMap((value) => {
      const gap = rec2(value);
      return gap && typeof gap.artifact === "string" && typeof gap.cause === "string" ? [{ artifact: gap.artifact, cause: gap.cause }] : [];
    })
  } : null;
  return {
    from,
    to,
    decodedCall,
    events,
    internalTxs,
    transfers,
    utxo,
    ton,
    sui,
    aptos: tonMeta?.family === "aptos" ? {
      ledgerVersion: s(tonMeta.ledger_version),
      vmStatus: s(tonMeta.vm_status),
      finality: s(tonMeta.finality),
      gasUsed: s(tonMeta.gas_used),
      gasUnitPrice: s(tonMeta.gas_unit_price),
      sequenceNumber: s(tonMeta.sequence_number),
      payloadType: s(tonMeta.payload_type),
      stateChangeCount: typeof tonMeta.state_change_count === "number" ? tonMeta.state_change_count : void 0,
      gaps: (asArray(tonMeta.decode_gaps) ?? []).flatMap((value) => {
        const gap = rec2(value);
        return gap && typeof gap.artifact === "string" && typeof gap.cause === "string" ? [{ artifact: gap.artifact, cause: gap.cause }] : [];
      })
    } : null,
    entityHints,
    addresses: buildAddressIndex({ from, to, decodedCall, events, internalTxs, transfers, utxo, sui, chainId: bodyChainId(body) }),
    status: parseTxStatus(body),
    meta: parseDecodeMeta(body)
  };
}
function decodeMetaRecord(body) {
  return rec2(extras(body).decode_meta) ?? {};
}
function parseProcessingStats(body) {
  const meta = decodeMetaRecord(body);
  const timings = {};
  for (const [k, v] of Object.entries(rec2(meta.timings) ?? {})) {
    const n = numOrUndef(v);
    if (n !== void 0) timings[k] = n;
  }
  return {
    decodedAt: s(meta.decoded_at),
    durationMs: numOrUndef(meta.duration_ms),
    decoderVersion: s(meta.decoder_version),
    catalogVersion: s(meta.catalog_version),
    semanticSchemaVersion: s(meta.semantic_schema_version),
    timings,
    steps: parseProcessingSteps(body)
  };
}
function parseDecodeConfig(body) {
  const meta = rec2(decodedTx(body)?.metadata) ?? {};
  const cfg = rec2(meta.decode_config) ?? rec2(meta.decode_meta) ?? {};
  const phases = {};
  for (const [name, raw] of Object.entries(rec2(cfg.phases) ?? {})) {
    const o = rec2(raw);
    if (!o) continue;
    phases[name] = { value: o.value === true, source: s(o.source) };
  }
  const abiDeps = {};
  for (const [dep, state] of Object.entries({ ...rec2(cfg.deps), ...rec2(cfg.abi_deps) })) {
    const v = s(state);
    if (v) abiDeps[dep] = v;
  }
  return {
    phases,
    abiDeps,
    missingAbiTiers: strList(cfg.missing_abi_tiers),
    ...s(cfg.family) ? { family: s(cfg.family) } : {},
    ...s(cfg.proxy_onchain_rpc) ? { proxyOnchainRpc: s(cfg.proxy_onchain_rpc) } : {}
  };
}
function parseProcessingSteps(body) {
  const root = rec2(body) ?? {};
  const layers = asArray(rec2(root.extras)?.enrichment_layers) ?? asArray(root.enrichment_layers) ?? asArray(extras(body).enrichment_layers) ?? asArray(decodeCarrier(body).enrichment_layers) ?? [];
  const byStep = /* @__PURE__ */ new Map();
  for (const layer of layers) {
    for (const e of asArray(rec2(layer)?.enrichments) ?? []) {
      const eo = rec2(e);
      if (!eo) continue;
      const name = s(eo.step_name) ?? s(eo.source) ?? s(eo.kind);
      if (!name) continue;
      const at = s(eo.ts);
      const ms = at ? Date.parse(at) : NaN;
      const existing = byStep.get(name);
      const kind = s(eo.kind);
      const confidence = numOrUndef(eo.confidence);
      if (!existing) {
        byStep.set(name, {
          name,
          version: s(eo.step_version) ?? s(eo.version),
          at,
          kinds: kind ? [kind] : [],
          confidence,
          firstMs: Number.isFinite(ms) ? ms : void 0
        });
        continue;
      }
      if (kind && !existing.kinds.includes(kind)) existing.kinds.push(kind);
      if (confidence !== void 0) {
        existing.confidence = existing.confidence === void 0 ? confidence : Math.min(existing.confidence, confidence);
      }
    }
  }
  const steps = [...byStep.values()].sort(
    (a, b) => (a.firstMs ?? Number.POSITIVE_INFINITY) - (b.firstMs ?? Number.POSITIVE_INFINITY)
  );
  const origin = steps.find((s2) => s2.firstMs !== void 0)?.firstMs;
  return steps.map(({ firstMs, ...step }) => ({
    ...step,
    ...origin !== void 0 && firstMs !== void 0 ? { offsetMs: firstMs - origin } : {}
  }));
}
function bytecodeRecord(address, value) {
  const o = rec2(value);
  if (!o) return null;
  const strings2 = (raw) => Array.isArray(raw) ? raw.filter((v) => typeof v === "string") : [];
  return {
    address,
    codeSize: numOrUndef(o.code_size) ?? 0,
    ...s(o.code_hash) ? { codeHash: s(o.code_hash) } : {},
    capabilities: strings2(o.capabilities),
    notes: strings2(o.notes),
    constants: strings2(o.constants),
    constantsTotal: numOrUndef(o.constants_total) ?? strings2(o.constants).length,
    entryPoints: numOrUndef(o.entry_points) ?? 0,
    sourceRefs: (Array.isArray(o.source_refs) ? o.source_refs : []).flatMap((entry) => {
      const r = rec2(entry);
      const kind = r ? s(r.kind) : void 0;
      const ref = r ? s(r.ref) : void 0;
      return kind && ref ? [{ kind, ref }] : [];
    }),
    analyzers: (Array.isArray(o.analyzers) ? o.analyzers : []).flatMap((run) => {
      const r = rec2(run);
      const id = s(r?.id);
      const status = s(r?.status);
      if (!id || !status) return [];
      return [{ id, status, ...s(r?.reason) ? { reason: s(r?.reason) } : {} }];
    })
  };
}
function parseEffectInference(body) {
  const root = rec2(body) ?? {};
  const canonical = rec2(root.canonical_transaction) ?? root;
  const call = rec2(decodedTx(body)?.decoded_call) ?? rec2(rec2(root.decoded_transaction)?.decoded_call) ?? rec2(canonical.decoded_call);
  const raw = rec2(rec2(root.extras)?.effects) ?? rec2(extras(body).effects) ?? rec2(rec2(call?.metadata)?.effect_inference) ?? rec2(call?.effect_inference);
  if (!raw) return null;
  const rule = s(raw.rule);
  const label = s(raw.label);
  if (!rule || !label) return null;
  const strings2 = (value) => Array.isArray(value) ? value.filter((v) => typeof v === "string") : [];
  return {
    // Absent on records written before the flag existed, and those DID replace
    // the call — so the safe default is false, not true.
    supplements: raw.supplements === true,
    rule,
    label,
    protocol: s(raw.protocol) ?? null,
    confidence: numOrUndef(raw.confidence) ?? 0,
    reasons: strings2(raw.reasons),
    witnessInternalTxIndexes: Array.isArray(raw.witness_internal_tx_indexes) ? raw.witness_internal_tx_indexes.filter((v) => Number.isSafeInteger(v) && v >= 0) : [],
    scope: rec2(raw.scope) ?? void 0,
    correlations: (Array.isArray(raw.correlations) ? raw.correlations : []).flatMap((entry) => {
      const c = rec2(entry);
      if (!c || !Array.isArray(c.path) || !c.path.length || !c.path.every((v) => Number.isSafeInteger(v) && v >= 0)) return [];
      return [{
        path: c.path,
        was: s(c.was) ?? "",
        now: s(c.now) ?? "",
        basis: s(c.basis) ?? "",
        observedFrom: s(c.observed_from) ?? "",
        witnessIndex: numOrUndef(c.witness_index) ?? null,
        confidence: numOrUndef(c.confidence) ?? null
      }];
    }),
    witnessLogIndexes: Array.isArray(raw.witness_log_indexes) ? raw.witness_log_indexes.map((v) => numOrUndef(v)).filter((v) => typeof v === "number") : [],
    params: (Array.isArray(raw.params) ? raw.params : []).flatMap((entry) => {
      const p = rec2(entry);
      const name = p ? s(p.name) : void 0;
      if (!p || !name) return [];
      return [
        {
          name,
          type: s(p.type) ?? "",
          observedFrom: s(p.observed_from) ?? "log",
          index: numOrUndef(p.index) ?? null,
          ...Object.hasOwn(p, "value") ? { value: p.value } : {}
        }
      ];
    }),
    declined: (Array.isArray(raw.declined) ? raw.declined : []).flatMap((entry) => {
      const d = rec2(entry);
      const ruleId = d ? s(d.rule) : void 0;
      if (!d || !ruleId) return [];
      return [{ rule: ruleId, reason: s(d.reason) ?? "" }];
    })
  };
}
function parseBytecodeFacts(body) {
  const root = rec2(body) ?? {};
  const raw = rec2(rec2(root.extras)?.bytecode) ?? rec2(extras(body).bytecode);
  if (!raw) return [];
  const out = [];
  for (const [address, value] of Object.entries(raw)) {
    const record = bytecodeRecord(address, value);
    if (record) out.push(record);
  }
  return out.sort((a, b) => b.codeSize - a.codeSize);
}
var FORENSIC_SEVERITIES = ["info", "notice", "warning", "alert"];
function forensicSeverityRank(severity) {
  const rank = FORENSIC_SEVERITIES.indexOf(severity ?? "");
  return rank < 0 ? 0 : rank;
}
function forensicSeverity(value) {
  return FORENSIC_SEVERITIES[forensicSeverityRank(s(value))];
}
function forensicKindLabel(kind) {
  const bare = kind.includes(".") ? kind.slice(kind.indexOf(".") + 1) : kind;
  const words = bare.replace(/[._-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : kind;
}
function forensicRun(value) {
  const r = rec2(value);
  const id = s(r?.id);
  const status = s(r?.status);
  if (!r || !id || !status) return null;
  const durationMs = numOrUndef(r.duration_ms);
  return {
    id,
    status,
    ...s(r.reason) ? { reason: s(r.reason) } : {},
    ...s(r.version) ? { version: s(r.version) } : {},
    ...durationMs !== void 0 ? { durationMs } : {}
  };
}
function forensicFinding(value) {
  const f = rec2(value);
  const kind = s(f?.kind);
  const summary = s(f?.summary);
  if (!f || !kind || !summary) return null;
  const confidence = numOrUndef(f.confidence);
  const facts = rec2(f.facts);
  const dropped = facts && facts.truncated === true && Object.keys(facts).length === 2 ? numOrUndef(facts.bytes) : void 0;
  return {
    kind,
    severity: forensicSeverity(f.severity),
    summary,
    foundBy: (s(f.found_by) ?? "").split(",").map((id) => id.trim()).filter(Boolean),
    ...s(f.subject) ? { subject: s(f.subject) } : {},
    ...confidence !== void 0 && confidence >= 0 && confidence <= 1 ? { confidence } : {},
    ...dropped !== void 0 ? { factsDroppedBytes: dropped } : facts && Object.keys(facts).length > 0 ? { facts } : {}
  };
}
function parseForensicsRecord(body) {
  const root = rec2(body) ?? {};
  const raw = rec2(rec2(root.extras)?.forensics) ?? rec2(extras(body).forensics);
  if (!raw || numOrUndef(raw.v) !== 1) return null;
  const list = (value) => Array.isArray(value) ? value : [];
  const findings = list(raw.findings).map(forensicFinding).filter((f) => f !== null).sort((a, b) => forensicSeverityRank(b.severity) - forensicSeverityRank(a.severity));
  const stated = s(raw.highest_severity);
  const loudest = findings[0]?.severity ?? null;
  const highestSeverity = stated === void 0 ? loudest : loudest && forensicSeverityRank(loudest) > forensicSeverityRank(stated) ? loudest : forensicSeverity(stated);
  const escalation = rec2(raw.escalation);
  const durationMs = numOrUndef(raw.duration_ms);
  return {
    family: s(raw.family) ?? "unknown",
    highestSeverity,
    findings,
    findingsTotal: Math.max(numOrUndef(raw.findings_total) ?? 0, findings.length),
    probes: list(raw.probes).map(forensicRun).filter((r) => r !== null),
    sources: list(raw.sources).map(forensicRun).filter((r) => r !== null),
    ...escalation ? {
      escalation: {
        requested: numOrUndef(escalation.requested) ?? 0,
        published: numOrUndef(escalation.published) ?? 0,
        ...s(escalation.skipped_reason) ? { skippedReason: s(escalation.skipped_reason) } : {}
      }
    } : {},
    ...durationMs !== void 0 ? { durationMs } : {},
    ...s(raw.error) ? { error: s(raw.error) } : {}
  };
}
function processedOnForensicTier(body) {
  return rec2(parsePipelineExecution(body)?.preset)?.tier === "forensic";
}

// src/storage/bytecode-capabilities.ts
var BYTECODE_CAPABILITY_LABELS = {
  selfdestruct: { label: "can self-destruct", notable: true },
  delegatecall: { label: "runs other code as itself (delegatecall)", notable: true },
  callcode: { label: "callcode", notable: true },
  create2: { label: "deploys contracts (create2)", notable: true },
  create: { label: "deploys contracts" },
  external_call: { label: "calls out" },
  static_call: { label: "reads other contracts" },
  storage_read: { label: "reads storage" },
  storage_write: { label: "writes storage" },
  logs: { label: "emits logs" },
  balance_transfer: { label: "reads balances" },
  returndata: { label: "forwards return data" },
  extcode: { label: "inspects other contracts' code" },
  block_context: { label: "reads block context" }
};
function bytecodeCapabilityLabel(slug) {
  return BYTECODE_CAPABILITY_LABELS[slug] ?? { label: slug.replace(/_/g, " ") };
}
function orderBytecodeCapabilities(capabilities) {
  return [...capabilities].sort((a, b) => {
    const byReach = Number(!!bytecodeCapabilityLabel(b).notable) - Number(!!bytecodeCapabilityLabel(a).notable);
    return byReach !== 0 ? byReach : a.localeCompare(b);
  });
}

// src/storage/keyAttributeDecimals.ts
var TOKEN_FOR_AMOUNT = {
  amount: "token",
  amount_minimum: "token",
  fee_amount: "fee_token",
  dest_amount: "dest_token"
};
function wantsDecimals(attr) {
  return attr.kind === "token_amount" && typeof attr.decimals !== "number";
}
function tokenAddressIn(group, tokenKey) {
  for (const attr of group.attributes ?? []) {
    if (attr.key !== tokenKey) continue;
    const value = attr.value;
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return void 0;
}
function stampGroupTokenDecimals(groups, decimalsFor, symbolFor) {
  let anyChanged = false;
  const out = groups.map((group) => {
    let groupChanged = false;
    const attributes = (group.attributes ?? []).map((attr) => {
      if (!wantsDecimals(attr)) return attr;
      const tokenKey = TOKEN_FOR_AMOUNT[attr.key];
      if (!tokenKey) return attr;
      const address = tokenAddressIn(group, tokenKey);
      if (!address) return attr;
      const decimals = decimalsFor(address);
      if (typeof decimals !== "number") return attr;
      groupChanged = true;
      anyChanged = true;
      const symbol = attr.symbol ?? symbolFor?.(address);
      return symbol ? { ...attr, decimals, symbol } : { ...attr, decimals };
    });
    return groupChanged ? { ...group, attributes } : group;
  });
  return anyChanged ? out : groups;
}

// src/storage/group-view.ts
function parseTruncation(meta) {
  if (!meta) return void 0;
  const num2 = (v) => typeof v === "number" && Number.isFinite(v) ? v : void 0;
  const memberCount = num2(meta.member_count);
  const sceneLegCount = num2(meta.scene_leg_count);
  if (memberCount === void 0 && sceneLegCount === void 0) return void 0;
  return {
    memberCount: memberCount ?? 0,
    totalMemberCount: num2(meta.total_member_count),
    membersTruncated: meta.members_truncated === true,
    sceneLegCount: sceneLegCount ?? 0,
    sceneLegsCapped: meta.scene_legs_capped === true
  };
}
function rec3(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : void 0;
}
function parseLeg(value) {
  const o = rec3(value);
  const role = o && str(o.role);
  const chainId = o && (str(o.chain_id) ?? str(o.chainId));
  if (!o || !role || !chainId) return null;
  return {
    role,
    chainId,
    txHash: str(o.tx_hash) ?? str(o.txHash),
    label: str(o.label)
  };
}
function parseLegs(value) {
  return (Array.isArray(value) ? value : []).map(parseLeg).filter((l) => l !== null);
}
function strList2(value) {
  if (!Array.isArray(value)) return [];
  return value.map((v) => str(v)).filter((v) => v != null);
}
function num(value) {
  if (value == null || value === "") return void 0;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : void 0;
}
function parseGroupView(value) {
  const root = rec3(value) ?? {};
  const scene = parseGraphScene(root.scene ?? root);
  let flowScene;
  if (rec3(root.flow_scene)) {
    try {
      flowScene = parseGraphScene(root.flow_scene);
    } catch {
      flowScene = void 0;
    }
  }
  const attrs = scene.attributes ?? {};
  const group = rec3(root.group) ?? {};
  const highlights = parseKeyAttributeGroups(root.highlights ?? attrs.highlights);
  const legs = parseLegs(root.legs ?? attrs.legs);
  const protocolId = str(root.protocol_id) ?? str(root.protocolId) ?? highlights[0]?.protocolId;
  const state = str(group.state) ?? str(attrs.state);
  const closed = state === "completed" || state === "cancelled" || state === "reorged";
  const missingRoles = closed ? [] : strList2(group.missing_roles ?? attrs.missing_roles);
  const expectedRoles = strList2(group.expected_roles ?? attrs.expected_roles);
  const partial = Boolean(root.partial ?? attrs.partial ?? false);
  const awaiting = !closed && Boolean(
    group.awaiting ?? attrs.awaiting ?? (missingRoles.length > 0 || partial)
  );
  return {
    scene,
    highlights,
    legs,
    origin: str(root.origin) ?? str(attrs.origin),
    originChain: str(root.origin_chain) ?? str(attrs.origin_chain),
    messageId: str(root.message_id) ?? str(attrs.message_id),
    idLabel: str(group.id_label) ?? str(root.id_label),
    partial,
    protocolId,
    state,
    awaiting,
    missingRoles,
    expectedRoles,
    flowScene,
    truncation: parseTruncation(rec3(root.meta)),
    groupShape: str(group.group_shape ?? attrs.group_shape),
    expiresAt: num(group.expires_at) ?? num(attrs.expires_at)
  };
}

// src/storage/index.ts
var INLINE_BYTES_THRESHOLD = 1e4;
var LazyBody = class {
  constructor(fetch2) {
    this.fetch = fetch2;
  }
  fetch;
  static {
    brandClass(this, "storage/LazyBody");
  }
  cache = null;
  _loaded = false;
  inflight = null;
  async get() {
    if (this._loaded && this.cache !== null) return this.cache;
    if (!this.inflight) {
      this.inflight = this.fetch().then((v) => {
        this.cache = v;
        this._loaded = true;
        this.inflight = null;
        return v;
      });
    }
    return this.inflight;
  }
  get loaded() {
    return this._loaded;
  }
};
function loadEnvelope(header, fetchBody, inlineBody) {
  if (inlineBody) return { header, body: new LazyBody(async () => inlineBody) };
  if (!header.s3_key) {
    throw new Error(`Envelope ${header.canonical_tx_id} has neither an inline body nor an s3_key.`);
  }
  const key = header.s3_key;
  return { header, body: new LazyBody(() => fetchBody(key)) };
}
var SLICE_CORE = "core";
var SLICE_RAW = "raw";
var SLICE_EVENTS = "events";
var SLICE_DECODED = "decoded";
var OFFLOADABLE_SLICES = [SLICE_RAW, SLICE_EVENTS, SLICE_DECODED];
var SLICE_INFIXES = {
  [SLICE_RAW]: ".raw",
  [SLICE_EVENTS]: ".events",
  [SLICE_DECODED]: ".decoded"
};
var SLICE_FIELD_PATHS = {
  [SLICE_RAW]: ["raw_provider_payload"],
  [SLICE_EVENTS]: ["canonical_transaction", "events"],
  [SLICE_DECODED]: ["extras", "enrichment_layers"]
};
function sliceKey(coreS3Key, slice) {
  if (slice === SLICE_CORE) return coreS3Key;
  const infix = SLICE_INFIXES[slice];
  if (infix === void 0) return null;
  const marker = ".json";
  const idx = coreS3Key.lastIndexOf(marker);
  if (idx === -1) return null;
  return `${coreS3Key.slice(0, idx)}${infix}${coreS3Key.slice(idx)}`;
}
function bodyHasSlice(header, slice) {
  return !!header.body_slices && Object.prototype.hasOwnProperty.call(header.body_slices, slice);
}
function mergeSlice(body, slice, payload) {
  const path = SLICE_FIELD_PATHS[slice];
  if (!path || payload == null) return body;
  let cursor = body;
  for (const key of path.slice(0, -1)) {
    const next = cursor[key];
    if (typeof next !== "object" || next === null || Array.isArray(next)) {
      cursor[key] = {};
    }
    cursor = cursor[key];
  }
  cursor[path[path.length - 1]] = payload;
  return body;
}
var PLACEHOLDER_KEY = "_s3_offload";
function isOffloadPlaceholder(value) {
  return typeof value === "object" && value !== null && value[PLACEHOLDER_KEY] === true;
}
function makePlaceholder(opts) {
  return {
    [PLACEHOLDER_KEY]: true,
    bucket: opts.bucket,
    key: opts.key,
    size_bytes: opts.sizeBytes,
    attribute: opts.attribute
  };
}
async function rehydrateItem(item, fetcher) {
  const out = { ...item };
  await Promise.all(
    Object.entries(item).map(async ([attr, value]) => {
      if (!isOffloadPlaceholder(value)) return;
      try {
        out[attr] = await fetcher(value);
      } catch {
        out[attr] = value;
      }
    })
  );
  return out;
}
function safeLocalStorage() {
  try {
    const s2 = typeof localStorage !== "undefined" ? localStorage : void 0;
    return s2 && typeof s2.getItem === "function" ? s2 : void 0;
  } catch {
    return void 0;
  }
}
var STORAGE_NAMESPACE = "authe";
function storageKey(...parts) {
  return [STORAGE_NAMESPACE, ...parts.map(String).filter(Boolean)].join(":");
}
function readLocalJson(key, fallback) {
  const value = readLocalJsonOptional(key);
  return value === void 0 ? fallback : value;
}
function readLocalJsonOptional(key) {
  const store = safeLocalStorage();
  if (!store) return void 0;
  try {
    const raw = store.getItem(key);
    if (raw === null) return void 0;
    return JSON.parse(raw);
  } catch {
    return void 0;
  }
}
function writeLocalJson(key, value) {
  const store = safeLocalStorage();
  if (!store) return false;
  try {
    store.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}
function removeLocalKey(key) {
  safeLocalStorage()?.removeItem(key);
}
function localKeysWithPrefix(prefix) {
  const store = safeLocalStorage();
  if (!store) return [];
  const out = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (key != null && key.startsWith(prefix)) out.push(key);
  }
  return out;
}
function clearLocalPrefix(prefix) {
  const store = safeLocalStorage();
  if (!store) return 0;
  const keys = localKeysWithPrefix(prefix);
  for (const key of keys) store.removeItem(key);
  return keys.length;
}
var STORAGE_LEVELS = [
  "user",
  "tenant",
  "org",
  "customer",
  "app",
  "global"
];
function isGlobalish(value) {
  if (value == null) return true;
  const trimmed = value.trim();
  return trimmed === "" || trimmed === "main";
}
function identitySegments(identity, level) {
  const segs = [STORAGE_NAMESPACE];
  if (level === "global") return segs;
  if (identity.app) segs.push(String(identity.app));
  if (level === "app") return segs;
  if (!isGlobalish(identity.customerId)) segs.push("c", String(identity.customerId));
  if (level === "customer") return segs;
  if (!isGlobalish(identity.orgId)) segs.push("o", String(identity.orgId));
  if (level === "org") return segs;
  if (!isGlobalish(identity.tenantId)) segs.push("t", String(identity.tenantId));
  if (level === "tenant") return segs;
  if (identity.userId) segs.push("u", String(identity.userId));
  return segs;
}
var ScopedStorage = class _ScopedStorage {
  static {
    brandClass(this, "storage/ScopedStorage");
  }
  identity;
  /** The level `set`/`remove` use when none is given. */
  defaultLevel;
  constructor(identity) {
    this.identity = { ...identity };
    this.defaultLevel = identity.userId ? "user" : !isGlobalish(identity.tenantId) ? "tenant" : !isGlobalish(identity.orgId) ? "org" : "app";
  }
  /** The fully-namespaced key for `name` at `level`. */
  keyAt(name, level) {
    return [...identitySegments(this.identity, level), name].join(":");
  }
  /** Read exactly at one level (no fallback up the hierarchy). */
  getAt(name, level, fallback) {
    const value = readLocalJsonOptional(this.keyAt(name, level));
    return value === void 0 ? fallback : value;
  }
  /** Hierarchical read: most specific stored value wins, else `fallback`. */
  get(name, fallback) {
    const tried = /* @__PURE__ */ new Set();
    for (const level of STORAGE_LEVELS) {
      const key = this.keyAt(name, level);
      if (tried.has(key)) continue;
      tried.add(key);
      const value = readLocalJsonOptional(key);
      if (value !== void 0) return value;
    }
    return fallback;
  }
  /**
   * Invalidate a hierarchical read after another tab changes its storage.
   * Consumers re-read through `get`, including remove/clear fallback semantics.
   * This does not echo writes or carry preference values over another channel.
   */
  subscribe(name, onChange) {
    const store = safeLocalStorage();
    if (!store || typeof window === "undefined") return () => {
    };
    const keys = new Set(STORAGE_LEVELS.map((level) => this.keyAt(name, level)));
    const listener = (event) => {
      if (event.storageArea !== store || event.key !== null && !keys.has(event.key)) return;
      onChange();
    };
    window.addEventListener("storage", listener);
    return () => window.removeEventListener("storage", listener);
  }
  /** Write at `level` (default: `defaultLevel`). */
  set(name, value, level = this.defaultLevel) {
    return writeLocalJson(this.keyAt(name, level), value);
  }
  /** Remove at `level` (default: `defaultLevel`). */
  remove(name, level = this.defaultLevel) {
    removeLocalKey(this.keyAt(name, level));
  }
  /** Remove every key at `level` and narrower for this identity. Returns the count. */
  clear(level) {
    return clearLocalPrefix(`${identitySegments(this.identity, level).join(":")}:`);
  }
  /** Derive a new scope with some identity fields changed (e.g. switch tenant). */
  with(patch) {
    return new _ScopedStorage({ ...this.identity, ...patch });
  }
};
function createScopedStorage(identity) {
  return new ScopedStorage(identity);
}
function rewriteToCdn(url, config) {
  try {
    const u = new URL(url);
    if (config.s3Host && u.host !== config.s3Host) return url;
    u.host = config.cdnHost;
    u.protocol = "https:";
    if (config.cdnPathPrefix && !u.pathname.startsWith(config.cdnPathPrefix)) {
      u.pathname = `${config.cdnPathPrefix.replace(/\/$/, "")}${u.pathname}`;
    }
    return u.toString();
  } catch {
    return url;
  }
}
function cdnUrlForKey(s3Key, config) {
  const prefix = config.cdnPathPrefix ? `/${config.cdnPathPrefix.replace(/^\/|\/$/g, "")}` : "";
  const key = s3Key.replace(/^\//, "");
  return `https://${config.cdnHost}${prefix}/${key}`;
}

export { ApiClient, ApiEndpointMode, BYTECODE_CAPABILITY_LABELS, BulkLoader, CdnAccessDeniedError, DEBUG_QUERY_KEYS, DECODED_ENVELOPE_MARKER, DETAIL_TIER_ORDER, FORENSIC_SEVERITIES, INLINE_BYTES_THRESHOLD, LazyBody, NATIVE_TOKEN_PREFIX, OFFLOADABLE_SLICES, PLACEHOLDER_KEY, SLICE_CORE, SLICE_DECODED, SLICE_EVENTS, SLICE_FIELD_PATHS, SLICE_INFIXES, SLICE_RAW, STORAGE_LEVELS, STORAGE_NAMESPACE, ScopedStorage, ScopedTxCache, TOKEN_BATCH_KEYS, TOKEN_CATEGORY_LABEL, TOKEN_CATEGORY_UNKNOWN_REASON, TOKEN_FOR_AMOUNT, TOKEN_KIND_LABEL, TX_BODY_MAX_AGE_MS, TX_STATIC_MAX_AGE_MS, bodyHasSlice, bodyNativePrice, bodyTokenDefinitions, boundTokenRefs, buildAddressIndex, buildTokenMetadataIndex, bytecodeCapabilityLabel, capDistinct, cdnBaseUrl, cdnKeyDenialReason, cdnUrlForKey, chunkTokenRefs, clearCdnSession, clearLocalPrefix, cloneBody, coerceTransactionRow, coerceTransactionRows, coerceTxStatus, configureCdnDelivery, counterfeitTokenFlag, createEnvelopeBodyLoader, createIdbKvStore, createMemoryKvStore, createScopedStorage, createScopedTxCache, debugResourceSink, decodedOf, detailTierRank, detailTierVisible, displayOf, emptyTokenMetadataIndex, ensureCdnSession, errorEnvelopeToErrorBody, errorEnvelopeToErrorResponse, fetchSignedCdnJson, fetchTokenMetadata, filterSceneViewByDetailTier, foldTokenIdentifier, forensicKindLabel, forensicSeverityRank, getCdnSession, getDebugParams, graphEdgeId, graphTokenRefs, hasAssignmentActions, impersonationSentence, indexedDbAvailable, integrationOutputsFromBody, integrationRan, integrationRunsFromBody, invalidateCdnSession, isAddressAbbreviation, isAddressFactKind, isCdnKeyReadable, isCreationCallType, isDebugEnabled, isEnriched, isOffloadPlaceholder, isPrivateAssetCdnKey, isPrivateCdnKey, isPublicCdnKey, kindOf, listOutcome, loadEnvelope, localKeysWithPrefix, looksLikeAddress, makePlaceholder, maxDetailTier, mergeAssignmentState, mergeSlice, nativeTokenChain, nativeTokenIdentifier, nodesWithHiddenChildren, orderBytecodeCapabilities, parseBytecodeFacts, parseConfidenceGate, parseCrossChainRefs, parseDebugParams, parseDecodeConfig, parseDecodeMeta, parseDecodedCall, parseDecodedCallValue, parseEffectInference, parseEntityHints, parseEvents, parseForensicsRecord, parseGroupView, parseInternalTxs, parseKeyAttributeGroups, parseKeyAttributes, parsePipelineExecution, parseProcessingStats, parseProcessingSteps, parseRenderHints, parseTokenBatch, parseTokenFacts, parseTokenImpersonation, parseTransfers, parseTxBody, parseTxMeaning, parseTxRisk, parseTxStatus, parseUtxoInputsOutputs, pickActiveRegionUrl, prepareSignedCdnImage, processedOnForensicTier, rawOf, readDebugResources, readLocalJson, readLocalJsonOptional, readOutcome, readResponseDebug, rehydrateItem, removeLocalKey, resolveApiBaseUrl, resolveMediaRef, rewriteToCdn, sceneTokenDecimals, sceneTokenSymbols, searchTransactionPage, signedCdnUrl, sliceKey, stampGroupTokenDecimals, storageKey, thrownToErrorEnvelope, titledSymbol, toGraphView, toNetFlowRows, toSceneView, toTokenFlowRows, toUniqueAddresses, tokenCategoryLabel, tokenConfidence, tokenImpersonationSchema, tokenLogoRef, tokenMetadataKey, tokenPricesFromIndex, tokenStandard, totalEdgeUsd, totalEdgeUsdDisplay, transactionSearchCursor, triggerEnvelopeFromBody, txEntryUniqueAddresses, unwrapTxEnvelope, versionedKey, writeLocalJson };
//# sourceMappingURL=chunk-Z3CPHSXJ.js.map
//# sourceMappingURL=chunk-Z3CPHSXJ.js.map